/**
 * Available Partner Perks Data
 *
 * Array containing all available partner perk offerings including AWS Activate,
 * Notion, Slack, and many others. Each perk includes metadata, descriptions,
 * eligibility criteria, terms, and redemption instructions.
 *
 * Used to populate the partner perks marketplace interface.
 */
export const availablePerks = [
  {
    id: "aws5K",
    listId: 98,
    name: "AWS Activate", // company name
    logo: "/partners/AWS_RGB activate.png", // logo
    offer: "US$5,000 AWS Activate credits",
    description: "US$5,000 AWS Activate credits", // short description
    categories: ["Cloud computing", "Web hosting", "Cloud data storage"], // categories
    industry:
      "Computer processing and data preparation and processing services", // industry
    sicCode: "7374", // SIC code
    userAvailability: ["global"],
    bundleTiers: ["premium"], // bundle perks
    longDescription: `
          <p>
    <a title="https://aws.amazon.com/startups" href="https://aws.amazon.com/startups" target="_blank" rel="noopener noreferrer"><u>AWS Activate</u></a>
    is
    <a title="https://aws.amazon.com/" href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer"><u>Amazon Web Services</u></a>&apos;
    flagship startup program providing founders and their teams with
    <a title="https://aws.amazon.com/startups/credits" href="https://aws.amazon.com/startups/credits" target="_blank" rel="noopener noreferrer"><u>AWS Activate credits</u></a>
    based on their funding stages,
    <a title="https://startups.aws.com/learn/business-guidance" href="https://startups.aws.com/learn/business-guidance" target="_blank" rel="noopener noreferrer"><u>business mentorship</u></a>
    at every stage of their journey, and
    <a title="https://startups.aws.com/learn/technical-resources" href="https://startups.aws.com/learn/technical-resources" target="_blank" rel="noopener noreferrer"><u>technical support</u></a>.
    Whether building your MVP (<em>minimum viable product</em>), or scaling your existing solution, or
    <a title="https://aws.amazon.com/startups/ai-for-startups/" href="https://aws.amazon.com/startups/ai-for-startups/" target="_blank" rel="noopener noreferrer"><u>building your AI-native product with Amazon Bedrock or using Kiro to accelerate deployment</u></a>,
    AWS Activate helps to prove your world-changing solution from idea to launch.
  </p>
        `,
    longOffer: `
  <ul>
    <li>
      <p><strong>US$5,000 in AWS Activate credits</strong></p>
      <ul>
        <li>
          <p>Valid for a duration of 2 years for helping you test, build, and scale</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        Start building with
        <a title="https://aws.amazon.com/aws-startups/advisor/" href="https://aws.amazon.com/aws-startups/advisor/" target="_blank" rel="noopener noreferrer"><u>AWS Startup Advisor</u></a>,
        your AI-powered build assistant on AWS to get personalized guidance and context-aware recommendations on architecture, costs, security, and scalability
      </p>
    </li>

    <li>
      <p>
        Unlock
        <a title="https://startups.aws.com/offers" href="https://startups.aws.com/offers" target="_blank" rel="noopener noreferrer"><u>AWS Activate Exclusive Offers</u></a>
        from AWS Activate partners at discounted rates, attend
        <a title="https://startups.aws.com/programs" href="https://startups.aws.com/programs" target="_blank" rel="noopener noreferrer"><u>specialized programs</u></a>
        to connect with experts, and explore
        <a title="https://startups.aws.com/learn" href="https://startups.aws.com/learn" target="_blank" rel="noopener noreferrer"><u>resources</u></a>
        designed to help your startup learn, build, and grow
      </p>
    </li>

    <li>
      <p>
        Discover innovative startups building on AWS and join
        <a title="https://startups.aws.com/showcase" href="https://startups.aws.com/showcase" target="_blank" rel="noopener noreferrer"><u>AWS Activate Startup Showcase</u></a>
        to promote your own startup
      </p>
    </li>

    <li>
      <p>
        Read inspirational success stories of
        <a title="https://startups.aws.com/learn/featured-customers" href="https://startups.aws.com/learn/featured-customers" target="_blank" rel="noopener noreferrer"><u>featured AWS customers</u></a>
        to learn how AWS has empowered startups to achieve their goals
      </p>
    </li>

    <li>
      <p>
        Learn
        <a title="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" href="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" target="_blank" rel="noopener noreferrer"><u>everything you need to know about AWS Activate credits</u></a>
        to help your company at every stage of the startup journey
      </p>
    </li>

  
  </ul>
`,
    eligibilityCriteria: `
  <ul>
  <li>
  <p>
    You have to <strong>submit documents and information for roiquant’s financial due diligence review</strong> to pre-qualify for this credit package
  </p>
  <ul>
    <li>
      <p>
        This pre-qualification is based on <strong><u>soft</u> requirements</strong>:
      </p>
      <ul>
        <li>
          <p>Submit as many documents and as much information as possible</p>
        </li>
        <li>
          <p>Fulfill as many of the financial KPIs criteria as possible</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        <u>Soft requirements</u> are defined as <strong>should-have</strong> criteria that are <strong>optimization-driven</strong> with <strong>minor exemption</strong> flexibility
      </p>
      <ul>
        <li>
          <p>This credit package application <strong>does not require</strong> any documents or additional information for the financial due diligence review</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        For previously declined applications, providing supporting documents and relevant information may help improve the likelihood of approval
      </p>
      <ul>
        <li>
          <p>
            That said, <strong>the approval of AWS Activate perks redemption is not guaranteed and is determined at the sole discretion of AWS Startups</strong>
          </p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        AWS Startups requires our affiliated startups to <u>pre-apply</u> and <u>pre-qualify</u> for AWS Activate credits based on <strong>roiquant’s due diligence review process and criteria</strong>, before we pre-approve your pre-application
      </p>
    </li>
    <li>
      <p>
        <i>Note: More details about the documents and information, as well as the benchmarks of the financial KPIs criteria, will be shared to you via email</i>
      </p>
    </li>
  </ul>
</li>

  <li>
  <p>
    As
    <a title="https://startups.aws.com/providers" href="https://startups.aws.com/providers" target="_blank" rel="noopener noreferrer"><u>one of the AWS Activate Providers</u></a>,
    you <strong>must be affiliated with roiquant</strong> to apply for US$5,000 AWS Activate credits due to our healthy collaboration and good standing with AWS Startups
  </p>
</li>

    <li>
      <p>
        Your startup <strong>must be founded in the <u>last 10 years</u></strong>
        (<em>not more than 10 years old</em>)
      </p>
    </li>

    <li>
      <p>
        <strong>Must be self-funded</strong>, or <strong>have received funding with <u>angel</u>, or <u>preseed</u>, or <u>seed</u>, or <u>pre-series A</u>, or <u>series A</u></strong> <strong>investment</strong>
      </p>
      <ul>
        <li>
          <p>Most recent funding round must be within the last 12 months (<em>if applicable</em>)</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        You <strong>must have an AWS account on Paid Tier Plan</strong>
      </p>
    </li>

    <li>
      <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
    </li>

    <li>
      <p>
        <strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying
      </p>
    </li>

    <li>
      <p>
        Each of your application is
        <a title="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" href="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" target="_blank" rel="noopener noreferrer"><strong><u>for a greater amount of AWS Activate credits</u></strong></a>
        than your previously approved application or AWS Activate credit package
      </p>
      <ul>
        <li>
          <p>Have not previously received AWS Activate credits of equal or greater value</p>
        </li>
      </ul>
    </li>

   <li>
  <p>
    It is preferred that our affiliated startups apply through us
    (<em>due to our healthy collaboration and good standing with AWS Startups</em>)
    to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly
  </p>
</li>
  </ul>
`,
    footnote: "Available for all customers", // footnote,

    detailModalFootNote: `Reminder: Only available to roiquant customers (<em>very-early stage</em> )`,
    // Unused
    paywall: {
      header: "Get started with Standard Plan at USD $100 per year",
      notes: [
        "Partners' perks are only available for annual plans customers",
        "Once payment is made, there is no refund",
        "Downgrade your subscription plan at any time",
        "If you downgrade or cancel, you will have access to the perks available in your subscription plan, which you downgraded or cancelled from, until its 1 year period (from date of payment) expires",
      ],
    },
  },
  {
    id: "aws10K",
    listId: 99,
    name: "AWS Activate",
    logo: "/partners/AWS_RGB activate.png",
    offer: "US$10,000 AWS Activate credits",
    description: "US$10,000 AWS Activate credits",
    categories: ["Cloud computing", "Web hosting", "Cloud data storage"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `
  <p>
    <a title="https://aws.amazon.com/startups" href="https://aws.amazon.com/startups" target="_blank" rel="noopener noreferrer"><u>AWS Activate</u></a>
    is
    <a title="https://aws.amazon.com/" href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer"><u>Amazon Web Services</u></a>&apos;
    flagship startup program providing founders and their teams with
    <a title="https://aws.amazon.com/startups/credits" href="https://aws.amazon.com/startups/credits" target="_blank" rel="noopener noreferrer"><u>AWS Activate credits</u></a>
    based on their funding stages,
    <a title="https://startups.aws.com/learn/business-guidance" href="https://startups.aws.com/learn/business-guidance" target="_blank" rel="noopener noreferrer"><u>business mentorship</u></a>
    at every stage of their journey, and
    <a title="https://startups.aws.com/learn/technical-resources" href="https://startups.aws.com/learn/technical-resources" target="_blank" rel="noopener noreferrer"><u>technical support</u></a>.
    Whether building your MVP (<em>minimum viable product</em>), or scaling your existing solution, or
    <a title="https://aws.amazon.com/startups/ai-for-startups/" href="https://aws.amazon.com/startups/ai-for-startups/" target="_blank" rel="noopener noreferrer"><u>building your AI-native product with Amazon Bedrock or using Kiro to accelerate deployment</u></a>,
    AWS Activate helps to prove your world-changing solution from idea to launch.
  </p>
`,

    longOffer: `
  <ul>
    <li>
      <p><strong>US$10,000 in AWS Activate credits</strong></p>
      <ul>
        <li>
          <p>Valid for a duration of 2 years for helping you test, build, and scale</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        Start building with
        <a title="https://aws.amazon.com/aws-startups/advisor/" href="https://aws.amazon.com/aws-startups/advisor/" target="_blank" rel="noopener noreferrer"><u>AWS Startup Advisor</u></a>,
        your AI-powered build assistant on AWS to get personalized guidance and context-aware recommendations on architecture, costs, security, and scalability
      </p>
    </li>

    <li>
      <p>
        Unlock
        <a title="https://startups.aws.com/offers" href="https://startups.aws.com/offers" target="_blank" rel="noopener noreferrer"><u>AWS Activate Exclusive Offers</u></a>
        from AWS Activate partners at discounted rates, attend
        <a title="https://startups.aws.com/programs" href="https://startups.aws.com/programs" target="_blank" rel="noopener noreferrer"><u>specialized programs</u></a>
        to connect with experts, and explore
        <a title="https://startups.aws.com/learn" href="https://startups.aws.com/learn" target="_blank" rel="noopener noreferrer"><u>resources</u></a>
        designed to help your startup learn, build, and grow
      </p>
    </li>

    <li>
      <p>
        Discover innovative startups building on AWS and join
        <a title="https://startups.aws.com/showcase" href="https://startups.aws.com/showcase" target="_blank" rel="noopener noreferrer"><u>AWS Activate Startup Showcase</u></a>
        to promote your own startup
      </p>
    </li>

    <li>
      <p>
        Read inspirational success stories of
        <a title="https://startups.aws.com/learn/featured-customers" href="https://startups.aws.com/learn/featured-customers" target="_blank" rel="noopener noreferrer"><u>featured AWS customers</u></a>
        to learn how AWS has empowered startups to achieve their goals
      </p>
    </li>

    <li>
      <p>
        Learn
        <a title="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" href="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" target="_blank" rel="noopener noreferrer"><u>everything you need to know about AWS Activate credits</u></a>
        to help your company at every stage of the startup journey
      </p>
    </li>

 
  </ul>
`,

    eligibilityCriteria: `
  <ul>
  <li>
  <p>
    You have to <strong>submit documents and information for roiquant’s financial due diligence review</strong> to pre-qualify for this credit package
  </p>
  <ul>
    <li>
      <p>
        This pre-qualification is based on <strong><u>soft</u> requirements</strong>:
      </p>
      <ul>
        <li>
          <p>Submit as many documents and as much information as possible</p>
          <ul>
            <li>
              <p>However, a <strong>business plan</strong> is required</p>
            </li>
          </ul>
        </li>
        <li>
          <p>Fulfill as many of the financial KPIs criteria as possible</p>
          <ul>
            <li>
              <p>However, evidence of <strong>both revenue and AWS costs</strong> is required</p>
            </li>
          </ul>
        </li>
      </ul>
    </li>
    <li>
      <p>
        <u>Soft requirements</u> are defined as <strong>should-have</strong> criteria that are <strong>optimization-driven</strong> with <strong>minor exemption</strong> flexibility
      </p>
      <ul>
        <li>
          <p>This credit package application <strong>does require</strong> a supporting document and additional information for the financial due diligence review</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        For previously declined applications, providing supporting documents and relevant information may help improve the likelihood of approval
      </p>
      <ul>
        <li>
          <p>
            That said, <strong>the approval of AWS Activate perks redemption is not guaranteed and is determined at the sole discretion of AWS Startups</strong>
          </p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        AWS Startups requires our affiliated startups to <u>pre-apply</u> and <u>pre-qualify</u> for AWS Activate credits based on <strong>roiquant’s due diligence review process and criteria</strong>, before we pre-approve your pre-application
      </p>
    </li>
    <li>
      <p>
        <i>Note: More details about the documents and information, as well as the benchmarks of the financial KPIs criteria, will be shared to you via email</i>
      </p>
    </li>
  </ul>
</li>

  <li>
  <p>
    As
    <a title="https://startups.aws.com/providers" href="https://startups.aws.com/providers" target="_blank" rel="noopener noreferrer"><u>one of the AWS Activate Providers</u></a>,
    you <strong>must be affiliated with roiquant</strong> to apply for US$10,000 AWS Activate credits due to our healthy collaboration and good standing with AWS Startups
  </p>
</li>

    <li>
      <p>
        Your startup <strong>must be founded in the <u>last 10 years</u></strong>
        (<em>not more than 10 years old</em>)
      </p>
    </li>

    <li>
      <p>
        <strong>Must be self-funded</strong>, or <strong>have received funding with <u>angel</u>, or <u>preseed</u>, or <u>seed</u>, or <u>pre-series A</u>, or <u>series A</u></strong> <strong>investment</strong>
      </p>
      <ul>
        <li>
          <p>Most recent funding round must be within the last 12 months (<em>if applicable</em>)</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        You <strong>must have an AWS account on Paid Tier Plan</strong>
      </p>
    </li>

    <li>
      <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
    </li>

    <li>
      <p>
        <strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying
      </p>
    </li>

    <li>
      <p>
        Each of your application is
        <a title="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" href="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" target="_blank" rel="noopener noreferrer"><strong><u>for a greater amount of AWS Activate credits</u></strong></a>
        than your previously approved application or AWS Activate credit package
      </p>
      <ul>
        <li>
          <p>Have not previously received AWS Activate credits of equal or greater value</p>
        </li>
      </ul>
    </li>

    <li>
  <p>
    It is preferred that our affiliated startups apply through us
    (<em>due to our healthy collaboration and good standing with AWS Startups</em>)
    to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly
  </p>
</li>
  </ul>
`,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers (<em>early stage</em> )`,
  },
  {
    id: "aws25K",
    listId: 136,
    name: "AWS Activate",
    logo: "/partners/AWS_RGB activate.png",
    offer: "US$25,000 AWS Activate credits",
    description: "US$25,000 AWS Activate credits",
    categories: ["Cloud computing", "Web hosting", "Cloud data storage"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    actionLabel: "Apply now",
    longDescription: `
  <p>
    <a title="https://aws.amazon.com/startups" href="https://aws.amazon.com/startups" target="_blank" rel="noopener noreferrer"><u>AWS Activate</u></a>
    is
    <a title="https://aws.amazon.com/" href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer"><u>Amazon Web Services</u></a>&apos;
    flagship startup program providing founders and their teams with
    <a title="https://aws.amazon.com/startups/credits" href="https://aws.amazon.com/startups/credits" target="_blank" rel="noopener noreferrer"><u>AWS Activate credits</u></a>
    based on their funding stages,
    <a title="https://startups.aws.com/learn/business-guidance" href="https://startups.aws.com/learn/business-guidance" target="_blank" rel="noopener noreferrer"><u>business mentorship</u></a>
    at every stage of their journey, and
    <a title="https://startups.aws.com/learn/technical-resources" href="https://startups.aws.com/learn/technical-resources" target="_blank" rel="noopener noreferrer"><u>technical support</u></a>.
    Whether building your MVP (<em>minimum viable product</em>), or scaling your existing solution, or
    <a title="https://aws.amazon.com/startups/ai-for-startups/" href="https://aws.amazon.com/startups/ai-for-startups/" target="_blank" rel="noopener noreferrer"><u>building your AI-native product with Amazon Bedrock or using Kiro to accelerate deployment</u></a>,
    AWS Activate helps to prove your world-changing solution from idea to launch.
  </p>
`,

    longOffer: `
  <ul>
    <li>
      <p><strong>US$25,000 in AWS Activate credits</strong></p>
      <ul>
        <li>
          <p>Valid for a duration of 2 years for helping you build and scale</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        Start building with
        <a title="https://aws.amazon.com/aws-startups/advisor/" href="https://aws.amazon.com/aws-startups/advisor/" target="_blank" rel="noopener noreferrer"><u>AWS Startup Advisor</u></a>,
        your AI-powered build assistant on AWS to get personalized guidance and context-aware recommendations on architecture, costs, security, and scalability
      </p>
    </li>

    <li>
      <p>
        Unlock
        <a title="https://startups.aws.com/offers" href="https://startups.aws.com/offers" target="_blank" rel="noopener noreferrer"><u>AWS Activate Exclusive Offers</u></a>
        from AWS Activate partners at discounted rates, attend
        <a title="https://startups.aws.com/programs" href="https://startups.aws.com/programs" target="_blank" rel="noopener noreferrer"><u>specialized programs</u></a>
        to connect with experts, and explore
        <a title="https://startups.aws.com/learn" href="https://startups.aws.com/learn" target="_blank" rel="noopener noreferrer"><u>resources</u></a>
        designed to help your startup learn, build, and grow
      </p>
    </li>

    <li>
      <p>
        Discover innovative startups building on AWS and join
        <a title="https://startups.aws.com/showcase" href="https://startups.aws.com/showcase" target="_blank" rel="noopener noreferrer"><u>AWS Activate Startup Showcase</u></a>
        to promote your own startup
      </p>
    </li>

    <li>
      <p>
        Read inspirational success stories of
        <a title="https://startups.aws.com/learn/featured-customers" href="https://startups.aws.com/learn/featured-customers" target="_blank" rel="noopener noreferrer"><u>featured AWS customers</u></a>
        to learn how AWS has empowered startups to achieve their goals
      </p>
    </li>

    <li>
      <p>
        Learn
        <a title="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" href="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" target="_blank" rel="noopener noreferrer"><u>everything you need to know about AWS Activate credits</u></a>
        to help your company at every stage of the startup journey
      </p>
    </li>

  </ul>
`,

    eligibilityCriteria: `
  <ul>
   <li>
  <p>
    You need to <strong>submit documents and information for roiquant’s financial due diligence review</strong> to pre-qualify for this credit package
  </p>
  <ul>
    <li>
      <p>
        This pre-qualification is based on <strong><u>hard</u> requirements</strong>:
      </p>
      <ul>
        <li>
          <p>Must submit all documents and information</p>
        </li>
        <li>
          <p>Must fulfill all financial KPIs criteria to the greatest extent possible</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        <u>Hard requirements</u> are defined as <strong>must-have</strong> criteria that are <strong>constraint-driven</strong> with <strong>no</strong> flexibility
      </p>
      <ul>
        <li>
          <p>This credit package application <strong>requires</strong> supporting documents and additional information for the financial due diligence review</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        For previously declined applications, providing supporting documents and relevant information to satisfy the criteria may help improve the likelihood of approval
      </p>
      <ul>
        <li>
          <p>
            That said, <strong>the approval of AWS Activate perks redemption is not guaranteed and is determined at the sole discretion of AWS Startups</strong>
          </p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        AWS Startups requires our affiliated startups to <u>pre-apply</u> and <u>pre-qualify</u> for AWS Activate credits based on <strong>roiquant’s due diligence review process and criteria</strong>, before we pre-approve your pre-application
      </p>
    </li>
    <li>
      <p>
        <i>Note: More details about the documents and information, as well as the benchmarks of the financial KPIs criteria, will be shared to you via email</i>
      </p>
    </li>
  </ul>
</li>

 <li>
    <p>
      As
      <a title="https://startups.aws.com/providers" href="https://startups.aws.com/providers" target="_blank" rel="noopener noreferrer"><u>one of the AWS Activate Providers</u></a>,
      you <strong>must be affiliated with roiquant</strong> in order for us to <a title="https://startups.aws.com/providers" href="https://www.merriam-webster.com/dictionary/vouch-for" target="_blank" rel="noopener noreferrer">vouch for</a> your early-growing stage startup to apply for US$25,000 AWS Activate credits due to our healthy collaboration and good standing with AWS Startups
    </p>
      <ul>
        <li>
          <p>A gentle reminder, to vouch for something or someone means that <strong>we are saying/supporting that something or someone is <u>honest</u>, <u>true</u> and <u>good</u></strong>.</p>
        </li>
          <ul>    
            <li>
              <p><u>Examples of "<em>vouch for</em>" in a sentence</u>: <em>we can vouch for the authenticity of the documents</em>, <em>our finance team will vouch for the accuracy of the financial report</em>, <em>we know the founder for years and can vouch for his honesty</em>, etc.</p>
            </li>
          </ul>
      </ul>
  </li>

    <li>
      <p>
        Your startup <strong>must be founded in the <u>last 10 years</u></strong>
        (<em>not more than 10 years old</em>)
      </p>
    </li>

    <li>
      <p>
        <strong>Must be self-funded</strong>, or <strong>have received funding with <u>angel</u>, or <u>preseed</u>, or <u>seed</u>, or <u>pre-series A</u>, or <u>series A</u></strong> <strong>investment</strong>
      </p>
      <ul>
        <li>
          <p>Most recent funding round must be within the last 12 months (<em>if applicable</em>)</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        You <strong>must have an AWS account on Paid Tier Plan</strong>
      </p>
    </li>

    <li>
      <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
    </li>

    <li>
      <p>
        <strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying
      </p>
    </li>

    <li>
      <p>
        Each of your application is
        <a title="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" href="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" target="_blank" rel="noopener noreferrer"><strong><u>for a greater amount of AWS Activate credits</u></strong></a>
        than your previously approved application or AWS Activate credit package
      </p>
      <ul>
        <li>
          <p>Have not previously received AWS Activate credits of equal or greater value</p>
        </li>
      </ul>
    </li>

   <li>
  <p>
    It is preferred that our affiliated startups apply through us
    (<em>due to our healthy collaboration and good standing with AWS Startups</em>)
    to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly
  </p>
</li>
  </ul>
`,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers (<em>early-growing stage</em> )`,
  },
  {
    id: "aws200K",
    listId: 137,
    name: "AWS Activate",
    logo: "/partners/AWS_RGB activate.png",
    offer: "Up to US$200,000 AWS Activate credits",
    description: "Up to US$200,000 AWS Activate credits",
    categories: ["Cloud computing", "Web hosting", "Cloud data storage"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    actionLabel: "Apply now",
    longDescription: `
  <p>
    <a title="https://aws.amazon.com/startups" href="https://aws.amazon.com/startups" target="_blank" rel="noopener noreferrer"><u>AWS Activate</u></a>
    is
    <a title="https://aws.amazon.com/" href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer"><u>Amazon Web Services</u></a>&apos;
    flagship startup program providing founders and their teams with
    <a title="https://aws.amazon.com/startups/credits" href="https://aws.amazon.com/startups/credits" target="_blank" rel="noopener noreferrer"><u>AWS Activate credits</u></a>
    based on their funding stages,
    <a title="https://startups.aws.com/learn/business-guidance" href="https://startups.aws.com/learn/business-guidance" target="_blank" rel="noopener noreferrer"><u>business mentorship</u></a>
    at every stage of their journey, and
    <a title="https://startups.aws.com/learn/technical-resources" href="https://startups.aws.com/learn/technical-resources" target="_blank" rel="noopener noreferrer"><u>technical support</u></a>.
    Whether building your MVP (<em>minimum viable product</em>), or scaling your existing solution, or
    <a title="https://aws.amazon.com/startups/ai-for-startups/" href="https://aws.amazon.com/startups/ai-for-startups/" target="_blank" rel="noopener noreferrer"><u>building your AI-native product with Amazon Bedrock or using Kiro to accelerate deployment</u></a>,
    AWS Activate helps to prove your world-changing solution from idea to launch.
  </p>
`,

    longOffer: `
  <ul>
    <li>
      <p><strong>Up to US$200,000 in AWS Activate credits</strong></p>
    </li>

    <li>
      <p>
        Start building with
        <a title="https://aws.amazon.com/aws-startups/advisor/" href="https://aws.amazon.com/aws-startups/advisor/" target="_blank" rel="noopener noreferrer"><u>AWS Startup Advisor</u></a>,
        your AI-powered build assistant on AWS to get personalized guidance and context-aware recommendations on architecture, costs, security, and scalability
      </p>
    </li>

    <li>
      <p>
        Unlock
        <a title="https://startups.aws.com/offers" href="https://startups.aws.com/offers" target="_blank" rel="noopener noreferrer"><u>AWS Activate Exclusive Offers</u></a>
        from AWS Activate partners at discounted rates, attend
        <a title="https://startups.aws.com/programs" href="https://startups.aws.com/programs" target="_blank" rel="noopener noreferrer"><u>specialized programs</u></a>
        to connect with experts, and explore
        <a title="https://startups.aws.com/learn" href="https://startups.aws.com/learn" target="_blank" rel="noopener noreferrer"><u>resources</u></a>
        designed to help your startup learn, build, and grow
      </p>
    </li>

    <li>
      <p>
        Discover innovative startups building on AWS and join
        <a title="https://startups.aws.com/showcase" href="https://startups.aws.com/showcase" target="_blank" rel="noopener noreferrer"><u>AWS Activate Startup Showcase</u></a>
        to promote your own startup
      </p>
    </li>

    <li>
      <p>
        Read inspirational success stories of
        <a title="https://startups.aws.com/learn/featured-customers" href="https://startups.aws.com/learn/featured-customers" target="_blank" rel="noopener noreferrer"><u>featured AWS customers</u></a>
        to learn how AWS has empowered startups to achieve their goals
      </p>
    </li>

    <li>
      <p>
        Learn
        <a title="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" href="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" target="_blank" rel="noopener noreferrer"><u>everything you need to know about AWS Activate credits</u></a>
        to help your company at every stage of the startup journey
      </p>
    </li>

   
  </ul>
`,

    eligibilityCriteria: `
  <ul>
   <li>
  <p>
    You need to <strong>submit documents and information for roiquant’s financial due diligence review</strong> to pre-qualify for this credit package
  </p>
  <ul>
    <li>
      <p>
        This pre-qualification is based on <strong><u>hard</u> requirements</strong>:
      </p>
      <ul>
        <li>
          <p>Must submit all documents and information</p>
        </li>
        <li>
          <p>Must fulfill all financial KPIs criteria to the greatest extent possible</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        <u>Hard requirements</u> are defined as <strong>must-have</strong> criteria that are <strong>constraint-driven</strong> with <strong>no</strong> flexibility
      </p>
      <ul>
        <li>
          <p>This credit package application <strong>requires</strong> supporting documents and additional information for the financial due diligence review</p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        For previously declined applications, providing supporting documents and relevant information to satisfy the criteria may help improve the likelihood of approval
      </p>
      <ul>
        <li>
          <p>
            That said, <strong>the approval of AWS Activate perks redemption is not guaranteed and is determined at the sole discretion of AWS Startups</strong>
          </p>
        </li>
      </ul>
    </li>
    <li>
      <p>
        AWS Startups requires our affiliated startups to <u>pre-apply</u> and <u>pre-qualify</u> for AWS Activate credits based on <strong>roiquant’s due diligence review process and criteria</strong>, before we pre-approve your pre-application
      </p>
    </li>
    <li>
      <p>
        <i>Note: More details about the documents and information, as well as the benchmarks of the financial KPIs criteria, will be shared to you via email</i>
      </p>
    </li>
  </ul>
</li>

   <li>
      <p>
        As
        <a title="https://startups.aws.com/providers" href="https://startups.aws.com/providers" target="_blank" rel="noopener noreferrer"><u>one of the AWS Activate Providers</u></a>,
        you <strong>must be affiliated with roiquant</strong> in order for us to <a href="https://www.merriam-webster.com/dictionary/vouch-for" target="_blank" rel="noopener noreferrer">vouch for</a> your growth stage startup to apply for up to US$200,000 AWS Activate credits due to our healthy collaboration and good standing with AWS Startups
      </p>
        <ul>
          <li>
            <p>A gentle reminder, to vouch for something or someone means that <strong>we are saying/supporting that something or someone is <u>honest</u>, <u>true</u> and <u>good</u></strong>.</p>
          </li>
            <ul>    
              <li>
                <p><u>Examples of "<em>vouch for</em>" in a sentence</u>: <em>we can vouch for the authenticity of the documents</em>, <em>our finance team will vouch for the accuracy of the financial report</em>, <em>we know the founder for years and can vouch for his honesty</em>, etc.</p>
              </li>
            </ul>
        </ul>
    </li>

    <li>
      <p>
        Your startup <strong>must be founded in the <u>last 10 years</u></strong>
        (<em>not more than 10 years old</em>)
      </p>
    </li>

    <li>
      <p>
        <strong>Must be self-funded</strong>, or <strong>have received funding with <u>angel</u>, or <u>preseed</u>, or <u>seed</u>, or <u>pre-series A</u>, or <u>series A</u></strong> <strong>investment</strong>
      </p>
      <ul>
        <li>
          <p>Most recent funding round must be within the last 12 months (<em>if applicable</em>)</p>
        </li>
      </ul>
    </li>

    <li>
      <p>
        You <strong>must have an AWS account on Paid Tier Plan</strong>
      </p>
    </li>

    <li>
      <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
    </li>

    <li>
      <p>
        <strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying
      </p>
    </li>

    <li>
      <p>
        Each of your application is
        <a title="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" href="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" target="_blank" rel="noopener noreferrer"><strong><u>for a greater amount of AWS Activate credits</u></strong></a>
        than your previously approved application or AWS Activate credit package
      </p>
      <ul>
        <li>
          <p>Have not previously received AWS Activate credits of equal or greater value</p>
        </li>
      </ul>
    </li>

   <li>
  <p>
    It is preferred that our affiliated startups apply through us
    (<em>due to our healthy collaboration and good standing with AWS Startups</em>)
    to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly
  </p>
</li>
  </ul>
`,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers (<em>growth stage</em> )`,
  },
  {
    id: "aws200Kmore",
    listId: 139,
    name: "AWS Activate",
    logo: "/partners/AWS_RGB activate.png",
    offer: "More than US$200,000 AWS Credits for AI startups",
    description: "More than US$200,000 AWS Credits for AI startups",
    categories: ["Cloud computing", "Web hosting", "Cloud data storage"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    actionLabel: "Apply now",
    longDescription: `
    <p>
      <a title="https://aws.amazon.com/startups" href="https://aws.amazon.com/startups" target="_blank" rel="noopener noreferrer"><u>AWS Activate</u></a>
      is
      <a title="https://aws.amazon.com/" href="https://aws.amazon.com/" target="_blank" rel="noopener noreferrer"><u>Amazon Web Services</u></a>&apos;
      flagship startup program providing founders and their teams with
      <a title="https://aws.amazon.com/startups/credits" href="https://aws.amazon.com/startups/credits" target="_blank" rel="noopener noreferrer"><u>AWS Activate credits</u></a>
      based on their funding stages,
      <a title="https://startups.aws.com/learn/business-guidance" href="https://startups.aws.com/learn/business-guidance" target="_blank" rel="noopener noreferrer"><u>business mentorship</u></a>
      at every stage of their journey, and
      <a title="https://startups.aws.com/learn/technical-resources" href="https://startups.aws.com/learn/technical-resources" target="_blank" rel="noopener noreferrer"><u>technical support</u></a>.
      Whether building your MVP (<em>minimum viable product</em>), or scaling your existing solution, or
      <a title="https://aws.amazon.com/startups/ai-for-startups/" href="https://aws.amazon.com/startups/ai-for-startups/" target="_blank" rel="noopener noreferrer"><u>building your AI-native product with Amazon Bedrock or using Kiro to accelerate deployment</u></a>,
      AWS Activate helps to prove your world-changing solution from idea to launch.
    </p>
  `,

    longOffer: `
    <ul>
      <li>
  <p><strong>More than US$200,000 in AWS Credits for AI startups</strong></p>
  <ul>
    <li>
      <p><em><strong>By invitation only</strong></em>&nbsp; for AI startups ready to scale post AWS Activate Portfolio program</p>
    </li>
  </ul>
</li>

<li>
  <p>
    In other words, eligible AI startups who graduated from the AWS Activate Portfolio program may request for additional AWS credits via their respective AWS Account Managers
  </p>
  <ul>
    <li>
      <p>
        Additionally, roiquant is also able to vouch for our hyper-growth stage AI startups to apply for additional AWS credits due to our healthy collaboration and good standing with AWS Startups
      </p>
    </li>
  </ul>
</li>

      <li>
        <p>
          Start building with
          <a title="https://aws.amazon.com/aws-startups/advisor/" href="https://aws.amazon.com/aws-startups/advisor/" target="_blank" rel="noopener noreferrer"><u>AWS Startup Advisor</u></a>,
          your AI-powered build assistant on AWS to get personalized guidance and context-aware recommendations on architecture, costs, security, and scalability
        </p>
      </li>

      <li>
        <p>
          Unlock
          <a title="https://startups.aws.com/offers" href="https://startups.aws.com/offers" target="_blank" rel="noopener noreferrer"><u>AWS Activate Exclusive Offers</u></a>
          from AWS Activate partners at discounted rates, attend
          <a title="https://startups.aws.com/programs" href="https://startups.aws.com/programs" target="_blank" rel="noopener noreferrer"><u>specialized programs</u></a>
          to connect with experts, and explore
          <a title="https://startups.aws.com/learn" href="https://startups.aws.com/learn" target="_blank" rel="noopener noreferrer"><u>resources</u></a>
          designed to help your startup learn, build, and grow
        </p>
      </li>

      <li>
        <p>
          Discover innovative startups building on AWS and join
          <a title="https://startups.aws.com/showcase" href="https://startups.aws.com/showcase" target="_blank" rel="noopener noreferrer"><u>AWS Activate Startup Showcase</u></a>
          to promote your own startup
        </p>
      </li>

      <li>
        <p>
          Read inspirational success stories of
          <a title="https://startups.aws.com/learn/featured-customers" href="https://startups.aws.com/learn/featured-customers" target="_blank" rel="noopener noreferrer"><u>featured AWS customers</u></a>
          to learn how AWS has empowered startups to achieve their goals
        </p>
      </li>

      <li>
        <p>
          Learn
          <a title="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" href="https://aws.amazon.com/aws-startups/learn/everything-you-need-to-know-about-aws-activate-credits/" target="_blank" rel="noopener noreferrer"><u>everything you need to know about AWS Activate credits</u></a>
          to help your company at every stage of the startup journey
        </p>
      </li>

    </ul>
  `,

    eligibilityCriteria: `
    <ul>
     <li>
    <p>
      You need to <strong>submit documents and information for roiquant’s financial due diligence review</strong> to pre-qualify for this credit package
    </p>
    <ul>
      <li>
        <p>
          This pre-qualification is based on <strong><u>hard</u> requirements</strong>:
        </p>
        <ul>
          <li>
            <p>Must submit all documents and information</p>
          </li>
          <li>
            <p>Must fulfill all financial KPIs criteria to the greatest extent possible</p>
          </li>
        </ul>
      </li>
      <li>
        <p>
          <u>Hard requirements</u> are defined as <strong>must-have</strong> criteria that are <strong>constraint-driven</strong> with <strong>no</strong> flexibility
        </p>
        <ul>
          <li>
            <p>This credit package application <strong>requires</strong> supporting documents and additional information for the financial due diligence review</p>
          </li>
        </ul>
      </li>
      <li>
        <p>
          For previously declined applications, providing supporting documents and relevant information to satisfy the criteria may help improve the likelihood of approval
        </p>
        <ul>
          <li>
            <p>
              That said, <strong>the approval of AWS Activate perks redemption is not guaranteed and is determined at the sole discretion of AWS Startups</strong>
            </p>
          </li>
        </ul>
      </li>
      <li>
        <p>
          AWS Startups requires our affiliated startups to <u>pre-apply</u> and <u>pre-qualify</u> for AWS Activate credits based on <strong>roiquant’s due diligence review process and criteria</strong>, before we pre-approve your pre-application
        </p>
      </li>
      <li>
        <p>
          <i>Note: More details about the documents and information, as well as the benchmarks of the financial KPIs criteria, will be shared to you via email</i>
        </p>
      </li>
    </ul>
  </li>

     <li>
        <p>
          As
          <a title="https://startups.aws.com/providers" href="https://startups.aws.com/providers" target="_blank" rel="noopener noreferrer"><u>one of the AWS Activate Providers</u></a>,
          you <strong>must be affiliated with roiquant</strong> in order for us to <a href="https://www.merriam-webster.com/dictionary/vouch-for" target="_blank" rel="noopener noreferrer">vouch for</a> your hyper-growth stage startup to apply for more than US$200,000 AWS Credits for AI startups due to our  healthy collaboration and good standing with AWS Startups
        </p>
        <ul>
          <li>
            <p>A gentle reminder, to vouch for something or someone means that <strong>we are saying/supporting that something or someone is <u>honest</u>, <u>true</u> and <u>good</u></strong>.</p>
          </li>
            <ul>    
              <li>
                <p><u>Examples of "<em>vouch for</em>" in a sentence</u>: <em>we can vouch for the authenticity of the documents</em>, <em>our finance team will vouch for the accuracy of the financial report</em>, <em>we know the founder for years and can vouch for his honesty</em>, etc.</p>
              </li>
            </ul>
        </ul>
      </li>

      <li>
        <p>
          Your startup <strong>must be founded in the <u>last 10 years</u></strong>
          (<em>not more than 10 years old</em>)
        </p>
      </li>

      <li>
        <p>
          <strong>Must be self-funded</strong>, or <strong>have received funding with <u>angel</u>, or <u>preseed</u>, or <u>seed</u>, or <u>pre-series A</u>, or <u>series A</u></strong> <strong>investment</strong>
        </p>
        <ul>
          <li>
            <p>Most recent funding round must be within the last 12 months (<em>if applicable</em>)</p>
          </li>
        </ul>
      </li>

      <li>
        <p>
          You <strong>must have an AWS account on Paid Tier Plan</strong>
        </p>
      </li>

      <li>
        <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
      </li>

      <li>
        <p>
          <strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying
        </p>
      </li>

      <li>
        <p>
          Each of your application is
          <a title="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" href="https://aws.amazon.com/aws-startups/learn/applying-for-aws-activate-credits-a-step-by-step-guide/#amsc5--1vyczk3" target="_blank" rel="noopener noreferrer"><strong><u>for a greater amount of AWS Activate credits</u></strong></a>
          than your previously approved application or AWS Activate credit package
        </p>
        <ul>
          <li>
            <p>Have not previously received AWS Activate credits of equal or greater value</p>
          </li>
        </ul>
      </li>

     <li>
    <p>
      It is preferred that our affiliated startups apply through us
      (<em>due to our healthy collaboration and good standing with AWS Startups</em>)
      to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly
    </p>
  </li>
    </ul>
  `,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers (<em>hyper-growth stage</em> )`,
  },
  {
    id: "googleads",
    listId: 134,
    name: "Google Ads",
    logo: "/partners/google-ads.webp",
    offer: "Up to US$5,000 Google Ads credits",
    description: "Up to US$5,000 Google Ads credits",
    categories: ["Advertising", "Digital marketing"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailability: ["US"],
    userAvailabilityExtra: ["only"],
    bundleTiers: ["premium"],
    longDescription: `<p><a href="https://business.google.com/us/google-ads/" target="_blank" rel="noopener noreferrer">Google Ads</a> is <a href="https://business.google.com/us/" target="_blank" rel="noopener noreferrer">Google</a>'s online (<a href="https://business.google.com/us/resources/articles/what-is-paid-search/" target="_blank" rel="noopener noreferrer"><i>pay-per-click</i></a>) advertising platform enabling you to easily create online ads to reach people exactly when they're interested in the products and services that you offer. Powered by <a href="https://ai.google/" target="_blank" rel="noopener noreferrer">Google AI</a>, Google Ads enable you <i>to promote your business, help sell products or services, raise awareness, increase traffic to your website/app, and achieve your advertising goals.</i></p>`,
    longOffer: `
    <ul>
      <li>
        <p><strong>Claim <u>US$1,100</u> in ad credits:</strong> spend <u>US$1,600</u> with Google Ads in the <strong>first <u>60</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><strong>Claim <u>US$2,000</u> in ad credits:</strong> spend <u>US$3,500</u> with Google Ads in the <strong>first <u>60</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><strong>Claim <u>US$5,000</u> in ad credits:</strong> spend <u>US$10,000</u> with Google Ads in the <strong>first <u>60</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><u>Google Ads Platform Services</u></p>
        <ul>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/performance-max/" target="_blank" rel="noopener noreferrer">Performance Max</a></u></strong>: drive better results with a single campaign that helps you find valuable customers wherever they are across Google channels like Search, YouTube, Display, and more</p>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/search/" target="_blank" rel="noopener noreferrer">Search</a></u></strong>: reach customers actively searching online for the products and services you offer and help them choose your business </p>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/youtube-ads/" target="_blank" rel="noopener noreferrer">YouTube</a></u></strong>: drive the outcomes you care about on YouTube, powered by Google AI</p>
            <ul> 
              <li>
                <p>YouTube is the best place to not just efficiently drive reach, but build connections with engaged viewers for any marketing objective</p>
              </li>
            </ul>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/demand-generation/" target="_blank" rel="noopener noreferrer">Demand Gen</a></u></strong>: create and convert new demand on YouTube, Discover, and Gmail with visual storytelling tailored to specific audience</p>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/display-ads/" target="_blank" rel="noopener noreferrer">Display</a></u></strong>: promote your business with visually engaging ads when people are browsing relevant websites/apps, watching YouTube videos, and checking Gmail</p>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/app-ads/" target="_blank" rel="noopener noreferrer">App</a></u></strong>: promote your app and reach more people who are interested in apps like yours on Google Search, YouTube, Google Play, and more</p>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/shopping-ads/" target="_blank" rel="noopener noreferrer">Shopping</a></u></strong>: turn more shoppers into customers</p>
            <ul> 
              <li>
                <p>Promote your online and local inventory, and boost sales on your website or in your physical store</p>
              </li>
            </ul>
          </li>
          <li>
            <p><strong><u><a href="https://business.google.com/us/ad-solutions/local-service-ads/" target="_blank" rel="noopener noreferrer">Local Services Ads</a></u></strong>: show up when local customers search for your services, and build confidence with a Google badge</p>
          </li>
        </ul>
      </li>
    </ul>`,
    eligibilityCriteria: `
    <ul>
      <li>
        <p><strong>New advertiser</strong>: you're a <strong><u>new</u> advertiser</strong> to <u>Google Ads</u> and your first ad impression was served from your new Google Ads account in the <strong><i>first <u>30</u> days to unlock the ad credits</i></strong></p>
      </li>
      <li>
        <p><strong>Spend eligibility</strong>: you must be an active Google Ads customer meeting the minimum spend requirement (<i><strong><u>US$1,600</u></strong> or <strong><u>US$3,500</u></strong> or <strong><u>US$10,000</u></strong></i>) of the promotional offers to unlock the ad credits (<i><strong><u>US$1,100</u></strong> or <strong><u>US$2,000</u></strong> or <strong><u>US$5,000</u></strong></i>)</p>
          <ul> 
            <li>
              <p>The credits typically expire 60 days after it is applied to your account, unless specified otherwise in the additional terms and conditions for the relevant promotion</p>
            </li>
          </ul>
      </li>
      <li>
        <p><strong>Account access</strong>: you must have <strong><u>Standard</u> or <u>Admin</u> access</strong> to the Google Ads account</p>
      </li>
      <li>
        <p><strong>Account status</strong>: your account <strong>must be in <u>good standing</u></strong> and <strong>not be flagged for <u>fraudulent activity</u></strong></p>
      </li>
      <li>
        <p><strong>Country match</strong>: your startup/company <strong>must be a privately held for-profit business</strong> with billing address in the United States to receive ad credits</p>
      </li>
      <li>
        <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
      </li>
      <li>
        <p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p>
      </li>
      <li>
        <p>All Google Ads credits can be applied <a href="https://business.google.com/us/ad-tools/" target="_blank" rel="noopener noreferrer">towards Google Ads Platform Services</a></p>
      </li>
      <li>
        <p>Learn <a href="https://business.google.com/us/google-ads/how-ads-work/" target="_blank" rel="noopener noreferrer">how to set up your first Google Ads campaign in 5 simple steps</a></p>
      </li>
      <li>
        <p>Explore the <a href="https://business.google.com/us/resources/" target="_blank" rel="noopener noreferrer">library of resources and learn how to best use the tools to help you achieve your advertising goals</a></p>
      </li>
      <li>
        <p>Read <a href="https://ads.google.com/intl/en_us/home/terms-and-conditions/incentives/" target="_blank" rel="noopener noreferrer">terms and conditions apply for online advertising promotional credits</a></p>
      </li>
      <li>
        <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
      </li>
    </ul>
    `,
    footnote: "Available for global customers",
    detailModalFootNote:
      "Reminder: Only available to roiquant customers in US only",
  },
  {
    id: "tiktok",
    listId: 135,
    name: "TikTok Ads",
    logo: "/partners/tiktok.png",
    offer: "Up to US$6,000 TikTok Ads credits",
    description: "Up to US$6,000 TikTok Ads credits",
    categories: ["Advertising", "Digital marketing"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailabilityExtra: ["in most global countries"],
    bundleTiers: ["premium"],
    longDescription: `<p><a href="https://ads.tiktok.com/business/en/solutions/ads-manager" target="_blank" rel="noopener noreferrer">TikTok Ads Manager</a> is <a href="https://www.tiktok.com/about?lang=en" target="_blank" rel="noopener noreferrer">TikTok</a>'s all-in-one platform that simplifies campaign setup, drives efficient performance, and delivers results you can rely on. Powered by <a href="https://ads.tiktok.com/creative/creativestudio/home/en" target="_blank" rel="noopener noreferrer">TikTok AI</a>, TikTok Ads Manager enable you to create new jobs, drives revenue for businesses of all sizes, and contributes to the growth of economies around the world.</p>`,
    longOffer: `
    <ul>
      <li>
        <p><strong>Claim <u>US$200</u> in ad credits:</strong> spend <u>US$200</u> with TikTok Ads in the <strong>first <u>30</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><strong>Claim <u>US$500</u> in ad credits:</strong> spend <u>US$500</u> with TikTok Ads in the <strong>first <u>30</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><strong>Claim <u>US$1,000</u> in ad credits:</strong> spend <u>US$1,000</u> with TikTok Ads in the <strong>first <u>30</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><strong>Claim <u>US$4,000</u> in ad credits:</strong> spend <u>US$4,000</u> with TikTok Ads in the <strong>first <u>30</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><strong>Claim <u>US$6,000</u> in ad credits:</strong> spend <u>US$6,000</u> with TikTok Ads in the <strong>first <u>30</u> days</strong> to unlock the credits</p>
      </li>
      <li>
        <p><u>TikTok Ads Manager Solutions</u></p>
        <ul>
          <li>
            <p><strong><u><a href="https://ads.tiktok.com/business/en/products/ads" target="_blank" rel="noopener noreferrer">Branding</a></u></strong>: connect with users and show off your brand on the '<u>For You</u>' page and beyond</p>
          </li>
          <li>
            <p><strong><u><a href="https://ads.tiktok.com/business/en/products/creative" target="_blank" rel="noopener noreferrer">Creative</a></u></strong>: welcome to a playground where campaign creation is easier than ever</p>
          </li>
          <li>
            <p><strong><u><a href="https://ads.tiktok.com/business/en-US/products/ecommerce" target="_blank" rel="noopener noreferrer">Commerce</a></u></strong>: create shopping experiences that entertain and sell at the same time</p>
          </li>
          <li>
            <p><strong><u><a href="https://ads.tiktok.com/business/en/products/measurement" target="_blank" rel="noopener noreferrer">Measurement</a></u></strong>: track how your campaign is doing every step of the way and optimize as you go</p>
          </li>
          <li>
            <p><strong><u><a href="https://ads.tiktok.com/business/en-US/products/performance" target="_blank" rel="noopener noreferrer">Performance</a></u></strong>: supercharge your performance campaigns with automation you can fine-tune</p>
          </li>
        </ul>
      </li>
    </ul>`,
    eligibilityCriteria: `
<ul>
  <li>
    <p><strong>New advertiser</strong>: you're a <strong><u>new</u> advertiser</strong> to <strong><u><a href="https://ads.tiktok.com/business/en-US/blog/how-to-get-started-with-tiktok-ads-manager-your-step-by-step-guide" target="_blank" rel="noopener noreferrer">TikTok Ads Manager</a></u></strong> and your first ad impression was served from your new TikTok Ads Manager account in the <strong><em>first <u>30</u> days to unlock the ad credits</em></strong></p>
  </li>

  <li>
    <p><strong>Spend eligibility</strong>: you must be an active TikTok Ads customer hitting the eligible spend thresholds (<strong><em><u>US$200</u></em></strong> <em>or</em> <strong><em><u>US$500</u></em></strong> <em>or</em> <strong><em><u>US$1,000</u></em></strong> <em>or</em> <strong><em><u>US$4,000</u></em></strong> <em>or</em> <strong><em><u>US$6,000</u></em></strong>) of the promotional offers to unlock the ad credits (<strong><em><u>US$200</u></em></strong> <em>or</em> <strong><em><u>US$500</u></em></strong> <em>or</em> <strong><em><u>US$1,000</u></em></strong> <em>or</em> <strong><em><u>US$4,000</u></em></strong> <em>or</em> <strong><em><u>US$6,000</u></em></strong>)</p>
    <ul>
      <li>
        <p>The credits typically expire 30 days after it is applied to your account, unless specified otherwise in the additional terms and conditions for the relevant promotion</p>
      </li>
    </ul>
  </li>

  <li>
    <p><strong>Ad credits access</strong>: you must have a <strong><u>new</u></strong> and <strong><u>valid</u></strong>  TikTok Ads Manager account with capabilities of managing advertising campaigns and accessing ad credits</p>
    <ul>
      <li>
        <p>You must also use the TikTok Ads Manager to spend the ad credits, whereby the <u><a href="https://ads.tiktok.com/resources/help/article/advertising-credit-terms-and-conditions" target="_blank" rel="noopener noreferrer">terms, conditions and policies of the TikTok Ads Manager shall apply</a></u></p>
      </li>
    </ul>
  </li>

  <li>
    <p><strong>Account status</strong>: your account <strong>must be in <u>good standing</u></strong> and <strong>not be flagged for <u>fraudulent activity</u></strong></p>
  </li>

  <li>
    <p><strong>Country/region availability</strong>: TikTok Ads Promotional Codes (<em>ad credits, coupons, and promotions</em>) are offered across <u><a href="https://ads.tiktok.com/resources/help/article/about-the-incentive-program-for-new-advertisers" target="_blank" rel="noopener noreferrer">most countries/regions where TikTok Ads Manager is available</a></u>, including:</p>
    <ul>
      <li>
        <p>Algeria, Argentina, Australia, Austria, Azerbaijan, Bahrain, Bangladesh, Belarus, Belgium, Bolivia, Brazil, Cambodia, Canada, Chile, Colombia, Costa Rica, Czech Republic, Dominican Republic, Ecuador, Egypt, Finland, France, Germany, Greece, Guatemala, Hungary, Indonesia, Iraq, Ireland, Israel, Italy, Japan, Kazakhstan, Kenya, Kuwait, Lebanon, Malaysia, Mexico, Morocco, Netherlands, New Zealand, Nigeria, Norway, Oman, Pakistan, Panama, Paraguay, Philippines, Poland, Portugal, Puerto Rico, Saudi Arabia, Singapore, South Africa, South Korea, Spain, Sri Lanka, Sweden, Switzerland, Thailand, Turkey, United Arab Emirates, United Kingdom, United States, Uruguay, Vietnam</p>
      </li>
    </ul>
  </li>

  <li>
    <p><strong>Country match</strong>: your startup/company <strong>must be a privately held for-profit business</strong> with <u>billing address</u> matching your <strong>local region</strong> which your TikTok Ads Manager account was registered at, in order to receive ad credits</p>
  </li>

  <li>
    <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
  </li>

  <li>
    <p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p>
  </li>

  <li>
    <p>All TikTok Ads credits can be applied towards <u><a href="https://ads.tiktok.com/business/en/solutions" target="_blank" rel="noopener noreferrer">TikTok Ads Manager Solutions</a></u></p>
  </li>

  <li>
    <p>Check out the <u><a href="https://ads.tiktok.com/business/en/how-it-works" target="_blank" rel="noopener noreferrer">ultimate guide for creating your first TikTok ad campaign</a></u></p>
  </li>

  <li>
    <p>Learn with <u><a href="https://ads.tiktok.com/business/en/academy" target="_blank" rel="noopener noreferrer">TikTok Academy</a></u>, get <u><a href="https://ads.tiktok.com/business/en/academy/tiktok-certification" target="_blank" rel="noopener noreferrer">TikTok Certified</a></u>, explore <u><a href="https://ads.tiktok.com/business/en/inspiration" target="_blank" rel="noopener noreferrer">case studies</a></u>, read <u><a href="https://ads.tiktok.com/business/en/blog" target="_blank" rel="noopener noreferrer">latest news</a></u>, and know your audience better with <u><a href="https://ads.tiktok.com/business/en/insights/" target="_blank" rel="noopener noreferrer">TikTok Insights</a></u></p>
  </li>

  <li>
    <p>Read <u><a href="https://ads.tiktok.com/resources/help/article/tiktok-ads-promotional-codes" target="_blank" rel="noopener noreferrer">terms and conditions apply for online advertising promotional credits</a></u></p>
  </li>
  <li>
    <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
   </li>
</ul>
    `,
    footnote: "Available for customers in most countries",
    detailModalFootNote: `Reminder: Only available to roiquant customers in <a href="https://ads.tiktok.com/resources/help/article/about-the-incentive-program-for-new-advertisers" target="_blank" rel="noopener noreferrer">most global countries</a>`,
  },
  {
    id: "einPresswire_freeTrial",
    listId: 124,
    name: "EIN Presswire",
    logo: "/partners/ein.png",
    offer: "1 free press release distribution on EIN Presswire",
    description: "1 free press release distribution on EIN Presswire",
    categories: ["Press release distribution", "Marketing communications"],
    industry: "News syndicates",
    sicCode: "7383",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `<p>The <a title="https://www.einpresswire.com/" href="https://www.einpresswire.com/" target="_blank" rel="noopener noreferrer"><u>EIN Presswire</u></a> is everyone's internet news presswire. Built to help organizations reach more, get found, and build trust, while also creating a stronger digital record that search engines (<em>supporting SEO, GEO, and AI discoverability</em>) and AI systems (<em>such as, ChatGPT, Claude, and Gemini</em>) can discover and understand. Importantly, EIN Presswire is <a title="https://www.einpresswire.com/pricing" href="https://www.einpresswire.com/pricing" target="_blank" rel="noopener noreferrer"><u>incredibly affordable</u></a> due to its own proprietary distribution technologies.</p>`,
    longOffer: `
  <ul>
    <li>
      <p><strong>Send 1 free press release (one-time: free trial worth USD $149)</strong></p>
      <ul>
        <li>
          <p>Pay USD $0 for <u>1 press release</u> distribution (<em>roiquant's perk</em>)</p>
        </li>
        <li>
          <p>Price per press release: USD $149</p>
        </li>
        <li>
          <p>Word limit: 700 words</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einpresswire.com/major-newswires" href="https://www.einpresswire.com/major-newswires" target="_blank" rel="noopener noreferrer"><u>Major newswires</u></a></strong>: EIN Presswire distributes through major newswires and news platform, including the Associated Press (AP News), which reaches more than half of the world's population</p>
      <ul>
        <li>
          <p>As a Google News publisher, EIN Presswire also helps expand the search visibility and discoverability of your press releases by placing them where audiences are actively looking for relevant news</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einpresswire.com/search-engines-ai-databases" href="https://www.einpresswire.com/search-engines-ai-databases" target="_blank" rel="noopener noreferrer"><u>Search engines and AI databases</u></a></strong>: EIN Presswire helps make your press releases discoverable across major search engines such as Google, Yahoo, and Bing</p>
      <ul>
        <li>
          <p>Broad distribution also creates more pathways for AI-powered answer engines like ChatGPT, Claude, and Gemini to find, understand and surface your news, supporting SEO, GEO, and a stronger digital footprint for your brand</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einpresswire.com/news-databases" href="https://www.einpresswire.com/news-databases" target="_blank" rel="noopener noreferrer"><u>News databases</u></a></strong>: millions of professionals rely on leading news databases, aggregators, and research platforms such as Bloomberg Terminals, Muck Rack, Moody's NewsEdge, Naviga, Crunchbase, and MENAFN to monitor companies, industries, and market developments</p>
      <ul>
        <li>
          <p>EIN Presswire helps place your press releases into these information channels, increasing visibility with journalists, analysts, researchers, investors, and other decision-makers</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einpresswire.com/major-news-platforms" href="https://www.einpresswire.com/major-news-platforms" target="_blank" rel="noopener noreferrer"><u>Major news and broadcast platforms</u></a></strong>: EIN Presswire extends your news across a major US local media network, including ABC, NBC, FOX, CBS, and CW affiliate websites, along with radio station sites nationwide</p>
      <ul>
        <li>
          <p>This distribution footprint serves media platforms reaching approximately 68% of US households and more than 150 million unique monthly users, helping your story reach audiences at both the national and local level</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.affinitygrouppublishing.com/" href="https://www.affinitygrouppublishing.com/" target="_blank" rel="noopener noreferrer"><u>Affinity Group Publications</u></a></strong>: EIN Presswire includes access to Affinity Group Publications (AGP), a global network of 3,900+ niche news sites covering industries, regions, and communities around the world</p>
      <ul>
        <li>
          <p>These publications help extend your reach beyond major media by placing your news in front of a more targeted audience</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einpresswire.com/usa-today-network" href="https://www.einpresswire.com/usa-today-network" target="_blank" rel="noopener noreferrer"><u>USA TODAY Network</u></a></strong>: EIN Presswire distributes releases across the USA TODAY Network, which includes many of the United States' leading local newspapers and news brands, with a combined 96 Pulitzer Prizes won across the network</p>
      <ul>
        <li>
          <p>Reaching more than 130 million unique readers and online visitors each month, the network helps extend your story across trusted national and local media destinations throughout the US</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einnews.com/" href="https://www.einnews.com/" target="_blank" rel="noopener noreferrer"><u>EIN Presswire Newswires</u></a></strong>: EIN Presswire Newswires is its proprietary global news network, organizing and distributing news by US state, country, and industry</p>
      <ul>
        <li>
          <p>This targeted structure helps press releases reach relevant journalists, newsrooms, readers, and niche audiences while extending your story across a broad international information network</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong><a title="https://www.einpresswire.com/world-media-directory" href="https://www.einpresswire.com/world-media-directory" target="_blank" rel="noopener noreferrer"><u>World Media Directory</u></a></strong>: the EIN Presswire World Media Directory is a premier comprehensive database of media contacts, including journalists, editorial desks, and newsrooms, for maximizing media visibility</p>
      <ul>
        <li>
          <p>Press releases are distributed directly to these key media outlets, ensuring targeted and effective reach</p>
        </li>
      </ul>
    </li>
  </ul>`,
    eligibilityCriteria: `
  <ul>
    <li>
      <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>EIN Presswire customer</strong></p>
    </li>
    <li>
      <p>This free trial distribution (<em>1 free press release credit</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once</u></strong></p>
    </li>
    <li>
      <p>It is preferred that our affiliated startups redeem with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to redeeming directly</p>
    </li>
  </ul>
  `,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers`,
  },
  {
    id: "superfiliate",
    listId: 145,
    name: "Superfiliate",
    logo: "/partners/superfiliate.webp",
    offer: "15% discount on Superfiliate",
    description: "15% discount on Superfiliate",
    categories: ["Creator marketing software", "Affiliate management software"],
    industry: "Services-prepackaged software",
    sicCode: "7372",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `<p><a title="https://www.superfiliate.com/" href="https://www.superfiliate.com/" target="_blank" rel="noopener noreferrer"><u>Superfiliate</u></a> replaces the fragmentation of multiple tools and workflows into a single unified platform designed for creator-led growth. Brands can manage every type of creator relationship, from ambassadors and affiliates to influencers and paid partners, within one system. Unified creator profiles, automated workflows, and built-in discovery tools make it possible to scale programs without losing control or visibility.</p>`,
    longOffer: `
  <ul>
    <li>
      <p><strong>15% off monthly plan for Influencer Hub</strong></p>
      <ul>
        <li>
          <p><a title="https://www.superfiliate.com/influencer-hub" href="https://www.superfiliate.com/influencer-hub" target="_blank" rel="noopener noreferrer"><u>Your all-in-one marketing hub</u></a>: run your end-to-end influencer marketing workflows and give your creators an experience they are excited to share</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong>15% off monthly plan for Meta Ads Suite</strong></p>
      <ul>
        <li>
          <p><a title="https://www.superfiliate.com/meta-ads-suite" href="https://www.superfiliate.com/meta-ads-suite" target="_blank" rel="noopener noreferrer"><u>Turn creator content into Meta Partnership Ads at scale</u></a>: the shared engine for influencer marketers and ad buyers to source, permission, track, and launch Partnership Ads in minutes</p>
        </li>
      </ul>
    </li>
    <li>
      <p><strong>15% off monthly plan for TikTok Shop</strong></p>
      <ul>
        <li>
          <p><a title="https://www.superfiliate.com/tiktok-shop" href="https://www.superfiliate.com/tiktok-shop" target="_blank" rel="noopener noreferrer"><u>Unlock the power of TikTok Shop with Superfiliate</u></a>: super charge your TikTok strategy with influencer and affiliate programs powered by Superfiliate’s integration</p>
        </li>
      </ul>
    </li>
    <li>
      <p>Leading brands such as <a title="https://www.superfiliate.com/case-studies/how-moonbrew-built-a-900k-creator-channel-from-zero-in-under-a-year" href="https://www.superfiliate.com/case-studies/how-moonbrew-built-a-900k-creator-channel-from-zero-in-under-a-year" target="_blank" rel="noopener noreferrer"><u>MoonBrew</u></a>, <a title="https://www.superfiliate.com/case-studies/how-graza-drove-7x-roas-using-superfiliates-cobranded-landing-pages" href="https://www.superfiliate.com/case-studies/how-graza-drove-7x-roas-using-superfiliates-cobranded-landing-pages" target="_blank" rel="noopener noreferrer"><u>Graza</u></a>, <a title="https://www.superfiliate.com/case-studies/how-cymbiotika-uses-superfiliate-to-enroll-and-engage-1-200-affiliates" href="https://www.superfiliate.com/case-studies/how-cymbiotika-uses-superfiliate-to-enroll-and-engage-1-200-affiliates" target="_blank" rel="noopener noreferrer"><u>Cymbiotika</u></a>, and <a title="https://www.superfiliate.com/case-studies/how-omnilux-drives-93-new-customer-revenue-with-superfiliates-gifting-automations" href="https://www.superfiliate.com/case-studies/how-omnilux-drives-93-new-customer-revenue-with-superfiliates-gifting-automations" target="_blank" rel="noopener noreferrer"><u>Omnilux</u></a> are building systems that turn creators into a consistent source of revenue by using Superfiliate to power high-output creator programs that feed their paid media engines with proven content and clear performance data</p>
    </li>
    <li>
      <p>With <a title="https://www.superfiliate.com/social-listening" href="https://www.superfiliate.com/social-listening" target="_blank" rel="noopener noreferrer"><u>social listening</u></a> and <a title="https://www.superfiliate.com/integrations" href="https://www.superfiliate.com/integrations" target="_blank" rel="noopener noreferrer"><u>direct integrations</u></a> across Meta, TikTok Shop, and YouTube, brands can identify high-impact creators and understand performance signals before investing</p>
    </li>
    <li>
      <p><a title="https://www.superfiliate.com/automations" href="https://www.superfiliate.com/automations" target="_blank" rel="noopener noreferrer"><u>Automated workflows</u></a> streamline gifting, affiliate activation, and ambassador management, while robust analytics show exactly which creators and programs are driving revenue across both organic and paid channels</p>
    </li>
    <li>
      <p>The result is a <a title="https://www.superfiliate.com/field-guide" href="https://www.superfiliate.com/field-guide" target="_blank" rel="noopener noreferrer"><u>creator-led growth</u></a> engine that compounds over time and becomes a durable and scalable revenue stream</p>
    </li>
  </ul>`,
    eligibilityCriteria: `
  <ul>
    <li>
      <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>Superfiliate customer</strong></p>
    </li>
    <li>
      <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
    </li>
    <li>
      <p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p>
    </li>
    <li>
      <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
    </li>
  </ul>
  `,
    footnote: "Available for global customers",
    detailModalFootNote: "Reminder: Only available to roiquant customers",
  },
  {
    id: "linear",
    listId: 144,
    name: "Linear for Startups",
    logo: "/partners/linear.png",
    offer: "Free Linear Business plan for 3 months",
    description: "Free Linear Business plan for 3 months",
    categories: ["Product development", "Developer tools"],
    industry: "Services-prepackaged software",
    sicCode: "7372",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `
    <p>The <a title="https://linear.app/startups" href="https://linear.app/startups" target="_blank" rel="noopener noreferrer"><u>Linear for Startups program</u></a> is designed to help early stage startups discover a better, faster way to build and ship great products in a shared and structured environment. A purpose-built system for modern teams with AI workflows at its core, Linear sets a new standard for planning and building products.</p>
  `,
    longOffer: `
    <ul>
      <li>
        <p><strong>3 months free on Business plan</strong></p>
        <ul>
          <li>
            <p>1 offer per workspace (<em>only admin can redeem</em>)</p>
          </li>
          <li>
            <p>Available to workspaces that have never paid</p>
          </li>
          <li>
            <p><strong>Must be affiliated</strong> with one of Linear for Startups' partners (<em>such as roiquant</em>)</p>
          </li>
        </ul>
      </li>
      <li>
        <p><a title="https://linear.app/intake" href="https://linear.app/intake" target="_blank" rel="noopener noreferrer"><strong><u>Intake and integrations</u></strong></a>: automatically turn conversations and customer feedback into actionable issues that are instantly routed, labeled, and prioritized for the right team</p>
      </li>
      <li>
        <p><a title="https://linear.app/plan" href="https://linear.app/plan" target="_blank" rel="noopener noreferrer"><strong><u>Planning and monitoring</u></strong></a>: plan and navigate from idea to launch</p>
        <ul>
          <li>
            <p>Align your team with product initiatives, strategic roadmaps, and clear up-to-date PRDs (<em>product requirement documents</em>)</p>
          </li>
        </ul>
      </li>
      <li>
        <p><a title="https://linear.app/ai" href="https://linear.app/ai" target="_blank" rel="noopener noreferrer"><strong><u>AI and automations</u></strong></a>: build and deploy AI agents that work alongside you as teammates</p>
        <ul>
          <li>
            <p>Work on complex tasks together or delegate entire issues end-to-end</p>
          </li>
        </ul>
      </li>
      <li>
        <p><a title="https://linear.app/build" href="https://linear.app/build" target="_blank" rel="noopener noreferrer"><strong><u>Build, review, and ship</u></strong></a>: streamline code reviews with clear diffs, better context, and few back-and-forth comments</p>
        <ul>
          <li>
            <p>Keep PRs (<em>pull requests</em>) moving without sacrificing quality</p>
          </li>
        </ul>
      </li>
    </ul>
  `,
    eligibilityCriteria: `
    <ul>
      <li>
        <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>Linear customer</strong></p>
      </li>
      <li>
        <p>Only companies with <strong>fewer than 50 employees</strong> are eligible</p>
      </li>
      <li>
        <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p>
      </li>
      <li>
        <p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p>
      </li>
      <li>
        <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
      </li>
    </ul>
  `,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers`,
  },
  {
    id: "notion",
    listId: 116,
    name: "Notion for Startups",
    logo: "/partners/notion.png",
    offer: "Free Notion Business plan with unlimited AI for 6 months",
    description: "Free Notion Business plan with unlimited AI for 6 months",
    categories: [
      "Knowledge management software",
      "Productivity software",
      "Cloud SaaS",
    ],
    industry: "Services-prepackaged software",
    sicCode: "7372",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `
      <p>The <a title="https://www.notion.com/startups" href="https://www.notion.com/startups" target="_blank" rel="noopener noreferrer"><u>Notion for Startups program</u></a> is designed for high-growth startups and tech companies to combine note-taking, knowledge sharing, project management, and wikis on all-in-one AI workspace. Manage high-level projects and specific tasks with custom Gantt and Kanban setups.</p>
      `,
    longOffer: `
          <ul>
            <li>
              <p><strong>6 months free on Business Plan with unlimited Notion AI included</strong></p>
              <ul>
                <li>
                  <p>Worth USD $12,000+ in value for startups (<em>non-paying Notion customers</em>) up to 100 employees with no funding cap</p>
                </li>
                <li>
                  <p><strong>Must be affiliated</strong> with one of Notion for Startups' partners</p>
                </li>
              </ul>
            </li>
            <li>
              <p><u>3 months free on Business Plan with unlimited Notion AI included</u></p>
              <ul>
                <li>
                  <p>For startups (<em>non-paying Notion customers</em>) up to 100 employees with no funding cap</p>
                </li>
                <li>
                  <p><strong>No affiliation required</strong>, open to everyone</p>
                </li>
              </ul>
            </li>
            <li>
              <p><strong>Notion perks</strong>: exclusive software discounts for each part of the founder's journey</p>
            </li>
            <li>
              <p><strong>Champions community</strong>: <a title="https://www.notion.so/notion/Notion-Champions-20f977eb5fdd40d4a7a396f1742c3ea5" href="https://www.notion.so/notion/Notion-Champions-20f977eb5fdd40d4a7a396f1742c3ea5" target="_blank" rel="noopener noreferrer"><u>learn and connect with a community of Startup Champions</u></a> who evangelize Notion at their companies</p>
            </li>
            <li>
              <p><strong>Notion certified consultants</strong>: one complimentary session with a consultant to set up your Notion workspace</p>
            </li>
          </ul>`,
    eligibilityCriteria: ` 
            <ul>
              <li>
                <p>You <strong>must be affiliated</strong> with one of Notion for Startups' partners (<em>such as roiquant</em>)</p>
              </li>
              <li>
                <p>You <strong>must have a Notion account and a valid Notion domain</strong></p>
              </li>
              <li>
                <p><strong>Must be a non-paying</strong>, <em>and was never in the past a paying</em>, <strong>Notion customer</strong> that is now upgrading to <a title="https://www.notion.com/pricing" href="https://www.notion.com/pricing" target="_blank" rel="noopener noreferrer"><u>Notion Business plan</u></a></p>
              </li>
              <li>
                <p><strong>Must not have received any other promotional Notion offer</strong></p>
              </li>
              <li>
                <p>This exclusive offer (<em>6 months free usage</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once</u></strong></p>
              </li>
              <li>
                <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website&nbsp;</p>
              </li>
              <li>
                <p><strong>Work email</strong>: a valid work email address</p>
              </li>
              <li>
                <p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p>
              </li>
              <li>
                <p>Learn more about <a title="https://www.notion.com/startups" href="https://www.notion.com/startups" target="_blank" rel="noopener noreferrer"><u>Notion for Startups frequently asked questions (FAQs)</u></a></p>
              </li>
              <li>
                <p>It is preferred that our affiliated startups apply with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly</p>
              </li>
            </ul>`,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers`,
  },
  {
    id: "mongodb",
    listId: 100,
    name: "MongoDB for Startups",
    logo: "/partners/MongoDB.png",
    offer: "Up to US$5,000 MongoDB Atlas credits",
    description: "Up to US$5,000 MongoDB Atlas credits",
    categories: [
      "NoSQL database",
      "Document-oriented database",
      "Multi-cloud database",
    ],
    industry: "Services-prepackaged software",
    sicCode: "7372",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `
      <p>The <a title="https://www.mongodb.com/solutions/startups" href="https://www.mongodb.com/solutions/startups" target="_blank" rel="noopener noreferrer"><u>MongoDB for Startups</u></a> is a program supporting early stage high-growth startups with free MongoDB Atlas credits, one-on-one technical advice, co-marketing opportunities, and access to its partner network, to build faster and scale further.</p>
      `,
    longOffer: `
      <ul>
      <li>
        <p><strong>USD $500</strong> <strong>in MongoDB Atlas credits</strong></p>
        <ul>
          <li><p>Valid for a duration of 1 year and redeemable for usage of the core Atlas Database for any use case</p></li>
          <li><p>In addition to extended data services for full-text and vector search, data visualization, real-time analytics, building AI and event-driven applications, graph workloads, and more, to supercharge your data infrastructure</p></li>
        </ul>
      </li>
      <li>
          <p><strong>Additional USD $4,500 in MongoDB Atlas credits for AI startups</strong></p>
          <ul>
            <li><p>Valid for another duration of 1 year, once you have graduated from the MongoDB for Startups program, apply to join the <a title="https://www.mongodb.com/solutions/startups/ai-innovators" href="https://www.mongodb.com/solutions/startups/ai-innovators" target="_blank" rel="noopener noreferrer"><u>MongoDB AI Innovators program</u></a></p></li>
            <li><p>Helping you expand your AI innovation with MongoDB&apos;s comprehensive and inclusive support</p></li>
            <li><p><u>AI Startup track</u>: get the same co-marketing opportunities and professional services support available in MongoDB for Startups program, <em>plus additional Atlas credits</em>, as well as the opportunity to enter your AI startup for a chance to become <a title="https://www.mongodb.com/company/ventures" href="https://www.mongodb.com/company/ventures" target="_blank" rel="noopener noreferrer"><u>one of our featured ventures</u></a></p></li>
            <li><p><u>AI Amplify track</u>: AI submissions are fast-tracked by MongoDB experts and evaluated for strategic partnerships and joint go-to-market motions, in order to get more eyes on your project and exposure to new markets</p></li>
            <li><p>Learn more about <a title="https://www.mongodb.com/solutions/use-cases/artificial-intelligence" href="https://www.mongodb.com/solutions/use-cases/artificial-intelligence" target="_blank" rel="noopener noreferrer"><u>Atlas AI capabilities via MongoDB&apos;s AI Hub</u></a></p></li>
          </ul>
      </li>
      <li>
          <p><strong>Expert technical advice tailored for you</strong>: match startups with expert advisors for dedicated 1-on-1 sessions to help you learn how to avoid and overcome any challenges you may encounter</p>
          <ul>
            <li><p>Helping you scale and solve problems with you as you grow</p></li>
          </ul>
      </li>
      <li>
          <p><strong>Go-to-market opportunities</strong>: collaborate with MongoDB&apos;s diverse community of startups and developers through events and co-marketing initiatives</p>
          <ul>
            <li><p>Working with MongoDB&apos;s network to amplify your startup&apos;s growth and to promote the innovative tech you are building&nbsp;</p></li>
          </ul>
      </li>
      <li>
          <p><strong>Access to MongoDB Developer ecosystem</strong>: engage with MongoDB&apos;s diverse developer community through participating in events and co-marketing initiatives</p>
          <ul>
            <li><p>Testing your products, gauging demand, drumming up interest in your startup, and building connections to potential users and partners</p></li>
          </ul>
      </li>
      <li>
          <p><strong>Access to&nbsp;</strong><a title="https://www.mongodb.com/lp/solutions/startups/hub/perk-marketplace" href="https://www.mongodb.com/lp/solutions/startups/hub/perk-marketplace" target="_blank" rel="noopener noreferrer"><strong><u>MongoDB for Startups Perk marketplace</u></strong></a>: all of the partner perks available to you through MongoDB for Startups program</p>
          <ul>
            <li><p>Find partner deals, limited-time offers, and exclusive promotions from trusted vendors to help expand your runway and burn less cash</p></li>
          </ul>
      </li>
      </ul>
      `,
    eligibilityCriteria: `
      <ul>
        <li><p>You <strong>must be affiliated or associated</strong> with one of MongoDB for Startups&apos; partners (<em>such as roiquant</em>)</p></li>
        <li><p><strong>Early stage startups</strong>: this program is intended for startups at Series A or earlier</p></li>
        <li><p><strong>Product or service</strong>: startups building a product or service (<em>software development, marketing, consulting and design agencies are not eligible</em>)</p></li>
        <li><p><strong>New friends only</strong>: companies (<em>first-time participants</em>) that have not previously been part of MongoDB for Startups</p></li>
        <li>
          <p>This exclusive offer (<em>up to US$5,000 MongoDB Atlas credits</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once</u></strong></p>
        </li>
        <li><p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website</p></li>
        <li><p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p></li>
        <li>
          <p><strong>For MongoDB AI Innovators program</strong>: startups must have already been accepted into the MongoDB for Startups program and graduated from it, in order to be eligible for the <a title="https://www.mongodb.com/solutions/startups/ai-innovators" href="https://www.mongodb.com/solutions/startups/ai-innovators" target="_blank" rel="noopener noreferrer"><u>MongoDB AI Innovators program</u></a></p>
          <ul>
            <li><p>Such as MongoDB for Startups Alumni</p></li>
          </ul>
        </li>
        <li><p>Learn more about <a title="https://www.mongodb.com/solutions/startups" href="https://www.mongodb.com/solutions/startups" target="_blank" rel="noopener noreferrer"><u>MongoDB for Startups frequently asked questions (FAQs)</u></a></p></li> 
        <li>
          <p>It is preferred that our affiliated startups apply with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly</p>
        </li>
      </ul>
      `,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers`,
  },
  {
    id: "brevo",
    listId: 133,
    name: "Brevo",
    logo: "/partners/brevo-dark-logo.jpg",
    offer: "50% discount with unlimited emails on Brevo",
    description: "50% discount with unlimited emails on Brevo",
    categories: ["Email marketing software", "CRM software"],
    industry: "Electronic mail service",
    sicCode: "4822-03",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `<p>The <a title="https://www.brevo.com/products/marketing-platform" href="https://www.brevo.com/products/marketing-platform" target="_blank" rel="noopener noreferrer"><u>Marketing Platform</u></a> is Brevo's <u>all-in-one</u> marketing software designed to increase conversions and revenue with email marketing, multichannel campaigns, and marketing automation.</p>`,
    longOffer: `
    <ul>
      <li>
        <p><strong>50% discount for Marketing Platform's annual Starter and Business plans</strong></p>
        <ul>
          <li>
            <p><u>Email marketing</u>: craft personalized campaigns that captivate your audience and sell your products</p>
          </li>
          <li>
            <p><u>SMS marketing</u>: send targeted messages straight to your customers' pockets</p>
          </li>
          <li>
            <p><u>WhatsApp marketing</u>: reach your audience where they are - <em>on the world's most popular messaging app</em></p>
          </li>
          <li>
            <p><u>Marketing automation</u>: from welcome series to abandoned carts, automate marketing campaigns to reach targets faster</p>
          </li>
          <li>
            <p><u>Forms</u>: grow your email list and manage information requests with easy-to-embed custom forms</p>
          </li>
          <li>
            <p><u>AI for emails</u>: use AI to create better email content and send campaigns at the best time for your contacts</p>
          </li>
          <li>
            <p><u>Landing pages</u>: drive traffic and generate leads with expertly crafted templates, or build your own</p>
          </li>
          <li>
            <p><u>Web and mobile push</u>: ignite interest and catalyze conversions with web and mobile push notifications</p>
          </li>
          <li>
            <p><u>Mobile wallet</u>: connect with customers via their mobile wallets and build customer loyalty</p>
          </li>
        </ul>
      </li>
      <li>
        <p><strong>Monthly email volume: unlimited emails per month included</strong></p>
        <ul>
          <li>
            <p>You get to send up to unlimited emails per month, <em>giving your startup a competitive edge</em></p>
          </li>
        </ul>
      </li>
    </ul>`,
    eligibilityCriteria: `
    <ul>
      <li>
        <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>Brevo customer</strong></p>
      </li>
      <li>
        <p>This exclusive offer (<em>50% discount with unlimited emails</em>) is a <strong>one-time benefit that can only be redeemed/used <u> once for first-time purchase </u></strong></p>
      </li>
      <li>
        <p>It is preferred that our affiliated startups purchase with our coupon code to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
      </li>
    </ul>
    `,
    footnote: "Available for global customers",
    detailModalFootNote: "Reminder: Only available to roiquant customers",
  },
  // comment this on 1st August
  {
    id: "intuit30",
    listId: 123,
    name: "Intuit QuickBooks",
    logo: "/partners/intuit_quickbooks.png",
    offer: "30% discount for 6 months on QuickBooks",
    description: "30% discount for 6 months on QuickBooks",
    categories: [
      "Accounting software",
      "Financial management software",
      "Cloud SaaS",
    ],
    industry: "Services-prepackaged software",
    sicCode: "7372",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `<p>The <a title="https://quickbooks.intuit.com/online" href="https://quickbooks.intuit.com/online" target="_blank" rel="noopener noreferrer"><u>Intuit QuickBooks</u></a> accounting software is designed to keep your financials organized and manage everything in one integrated platform. Whether your business is just starting out and you need to send your first invoice, or you're an established business and are looking for more insights to grow, QuickBooks has the tools to support your business growth.</p>`,
    longOffer: `
                  <ul>
            <li>
                          <p><strong>30% discount for 6 months on QuickBooks Online</strong></p>
                          <ul>
                              <li><p><a title="https://quickbooks.intuit.com/online" href="https://quickbooks.intuit.com/online" target="_blank" rel="noopener noreferrer"><u>QuickBooks Online</u></a> makes managing money simple for small and medium-sized businesses, <em>providing one place where you can get real-time insights on how your business is doing</em>, to track money, send invoices, manage bills, run reports, and so much more</p></li>
                              <li><p>With <u>Simple Start</u>, you'll be able to see your income, track your expenses, and know where your business stands</p></li>
                              <li><p>With <u>Essentials</u>, you'll have the ability to track bill status, record payments, and create recurring payments</p></li>
                              <li><p>With <u>Plus</u>, you can easily track labour costs, payroll, and expenses with job costing, to manage and view all parts of your business</p></li>
                              <li><p>With <u>Advanced</u>, you can track performance, work smarter, get more done, and reliably restore your books, to boost productivity and efficiency</p></li>
                              <li><p>Save over 51 hours per month on average by using QuickBooks</p></li>
                              <li><p>90% of customers save time by connecting a bank or credit card to QuickBooks</p></li>
              </ul>
                      </li>

                      <li>
                          <p><strong>30% discount for 6 months on QuickBooks Payroll</strong></p>
                          <ul>
                              <li><p><a title="https://quickbooks.intuit.com/payroll/" href="https://quickbooks.intuit.com/payroll/" target="_blank" rel="noopener noreferrer"><u>QuickBooks Payroll</u></a> works for small to midsize businesses - <em>from accountants and financial experts to non-profits, construction companies, and restaurants</em> - to pay your team and streamline your HR tasks to save precious time, from one easy-to-use platform</p></li>
                              <li><p>With <u>Payroll Core</u>, you can easily pay your team and have your payroll taxes done for you</p></li>
                              <li><p>With <u>Payroll Premium</u>, you can also manage your team, and access HR and employee benefits</p></li>
                              <li><p>With <u>Payroll Elite</u>, you'll have access to on-demand experts, simplifying payday, and protecting what matter most</p></li>
                              <li><p>QuickBooks Payroll is the #1 payroll provider for small businesses by helping 1.4 million businesses do payroll and file taxes</p></li>
                          </ul>
                      </li>

                      <li>
                          <p><strong>QuickBooks gives self-starters the features they need to get ahead</strong></p>
                          <ul>
                              <li><p><a title="https://go.roiquant.com/get-qb-solopreneurs-exclusive-discount" href="https://go.roiquant.com/get-qb-solopreneurs-exclusive-discount" target="_blank" rel="noopener noreferrer"><u>Solopreneurs</u></a> stay tax-time ready all year, uncover tax deductions, create and send invoices, track your mileage, and keep tabs on your finances on the go</p></li>
                              <li><p>Once you connect a bank account, expense tracking helps you separate your personal and business spending with just the swipe of a screen</p></li>
                              <li><p>Find an average of USD $30,645 in tax deductions per year</p></li>
                              <li><p>Find an average of USD $16,317 in tax savings per year</p></li>
                              <li><p>Cut your tax bill by an average of 38% per year</p></li>
                          </ul>
                      </li>
                  </ul>`,
    eligibilityCriteria: `
        <ul>
          <li>
            <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>QuickBooks customer</strong></p>
          </li>
          <li>
            <p>This exclusive offer (<em>30% discount for 6 months</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once for first-time purchase</u></strong></p>
          </li>
          <li>
            <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
          </li>
        </ul>`,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers`,
  },
  // {
  //   id: "intuit70",
  //   listId: 117,
  //   name: "Intuit QuickBooks",
  //   logo: "/partners/intuit_quickbooks.png",
  //   offer: "70% discount for 3 months on QuickBooks",
  //   description: "70% discount for 3 months on QuickBooks",
  //   categories: [
  //     "Accounting software",
  //     "Financial management software",
  //     "Cloud SaaS",
  //   ],
  //   industry: "Services-prepackaged software",
  //   sicCode: "7372",
  //   userAvailability: ["global"],
  //   bundleTiers: ["premium"],
  //   longDescription: `<p>The <a title="https://quickbooks.intuit.com/online" href="https://quickbooks.intuit.com/online" target="_blank" rel="noopener noreferrer"><u>Intuit QuickBooks</u></a> accounting software is designed to keep your financials organized and manage everything in one integrated platform. Whether your business is just starting out and you need to send your first invoice, or you're an established business and are looking for more insights to grow, QuickBooks has the tools to support your business growth.</p>`,
  //   longOffer: `
  //                 <ul>
  //           <li>
  //                         <p><strong>70% discount for 3 months on QuickBooks Online</strong></p>
  //                         <ul>
  //                             <li><p><a title="https://quickbooks.intuit.com/online" href="https://quickbooks.intuit.com/online" target="_blank" rel="noopener noreferrer"><u>QuickBooks Online</u></a> makes managing money simple for small and medium-sized businesses, <em>providing one place where you can get real-time insights on how your business is doing</em>, to track money, send invoices, manage bills, run reports, and so much more</p></li>
  //                             <li><p>With <u>Simple Start</u>, you'll be able to see your income, track your expenses, and know where your business stands</p></li>
  //                             <li><p>With <u>Essentials</u>, you'll have the ability to track bill status, record payments, and create recurring payments</p></li>
  //                             <li><p>With <u>Plus</u>, you can easily track labour costs, payroll, and expenses with job costing, to manage and view all parts of your business</p></li>
  //                             <li><p>With <u>Advanced</u>, you can track performance, work smarter, get more done, and reliably restore your books, to boost productivity and efficiency</p></li>
  //                             <li><p>Save over 51 hours per month on average by using QuickBooks</p></li>
  //                             <li><p>30% of customers save time by connecting a bank or credit card to QuickBooks</p></li>
  //             </ul>
  //                     </li>

  //                     <li>
  //                         <p><strong>70% discount for 3 months on QuickBooks Payroll</strong></p>
  //                         <ul>
  //                             <li><p><a title="https://quickbooks.intuit.com/payroll/" href="https://quickbooks.intuit.com/payroll/" target="_blank" rel="noopener noreferrer"><u>QuickBooks Payroll</u></a> works for small to midsize businesses - <em>from accountants and financial experts to non-profits, construction companies, and restaurants</em> - to pay your team and streamline your HR tasks to save precious time, from one easy-to-use platform</p></li>
  //                             <li><p>With <u>Payroll Core</u>, you can easily pay your team and have your payroll taxes done for you</p></li>
  //                             <li><p>With <u>Payroll Premium</u>, you can also manage your team, and access HR and employee benefits</p></li>
  //                             <li><p>With <u>Payroll Elite</u>, you'll have access to on-demand experts, simplifying payday, and protecting what matter most</p></li>
  //                             <li><p>QuickBooks Payroll is the #1 payroll provider for small businesses by helping 1.4 million businesses do payroll and file taxes</p></li>
  //                         </ul>
  //                     </li>

  //                     <li>
  //                         <p><strong>QuickBooks gives self-starters the features they need to get ahead</strong></p>
  //                         <ul>
  //                             <li><p><a title="https://go.roiquant.com/get-qb-solopreneurs-exclusive-discount" href="https://go.roiquant.com/get-qb-solopreneurs-exclusive-discount" target="_blank" rel="noopener noreferrer"><u>Solopreneurs</u></a> stay tax-time ready all year, uncover tax deductions, create and send invoices, track your mileage, and keep tabs on your finances on the go</p></li>
  //                             <li><p>Once you connect a bank account, expense tracking helps you separate your personal and business spending with just the swipe of a screen</p></li>
  //                             <li><p>Find an average of USD $30,645 in tax deductions per year</p></li>
  //                             <li><p>Find an average of USD $16,317 in tax savings per year</p></li>
  //                             <li><p>Cut your tax bill by an average of 38% per year</p></li>
  //                         </ul>
  //                     </li>
  //                 </ul>`,
  //   eligibilityCriteria: `
  //       <ul>
  //         <li>
  //           <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>QuickBooks customer</strong></p>
  //         </li>
  //         <li>
  //           <p>This exclusive offer (<em>70% discount for 3 months</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once for first-time purchase</u></strong></p>
  //         </li>
  //         <li>
  //           <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
  //         </li>
  //       </ul>`,
  //   footnote: "Available for all customers",
  //   detailModalFootNote: `Reminder: Only available to roiquant customers`,
  // },
  // {
  //   id: "intuit90",
  //   listId: 131,
  //   name: "Intuit QuickBooks",
  //   logo: "/partners/intuit_quickbooks.png",
  //   offer: "90% discount for 3 months on QuickBooks",
  //   description: "90% discount for 3 months on QuickBooks",
  //   categories: [
  //     "Accounting software",
  //     "Financial management software",
  //     "Cloud SaaS",
  //   ],
  //   industry: "Services-prepackaged software",
  //   sicCode: "7372",
  //   userAvailability: ["global"],
  //   bundleTiers: ["premium"],
  //   longDescription: `<p>The <a title="https://quickbooks.intuit.com/online" href="https://quickbooks.intuit.com/online" target="_blank" rel="noopener noreferrer"><u>Intuit QuickBooks</u></a> accounting software is designed to keep your financials organized and manage everything in one integrated platform. Whether your business is just starting out and you need to send your first invoice, or you're an established business and are looking for more insights to grow, QuickBooks has the tools to support your business growth.</p>`,
  //   longOffer: `
  //                 <ul>
  //           <li>
  //                         <p><strong>90% discount for 3 months on QuickBooks Online</strong></p>
  //                         <ul>
  //                             <li><p><a title="https://quickbooks.intuit.com/online" href="https://quickbooks.intuit.com/online" target="_blank" rel="noopener noreferrer"><u>QuickBooks Online</u></a> makes managing money simple for small and medium-sized businesses, <em>providing one place where you can get real-time insights on how your business is doing</em>, to track money, send invoices, manage bills, run reports, and so much more</p></li>
  //                             <li><p>With <u>Simple Start</u>, you'll be able to see your income, track your expenses, and know where your business stands</p></li>
  //                             <li><p>With <u>Essentials</u>, you'll have the ability to track bill status, record payments, and create recurring payments</p></li>
  //                             <li><p>With <u>Plus</u>, you can easily track labour costs, payroll, and expenses with job costing, to manage and view all parts of your business</p></li>
  //                             <li><p>With <u>Advanced</u>, you can track performance, work smarter, get more done, and reliably restore your books, to boost productivity and efficiency</p></li>
  //                             <li><p>Save over 51 hours per month on average by using QuickBooks</p></li>
  //                             <li><p>30% of customers save time by connecting a bank or credit card to QuickBooks</p></li>
  //             </ul>
  //                     </li>

  //                     <li>
  //                         <p><strong>90% discount for 3 months on QuickBooks Payroll</strong></p>
  //                         <ul>
  //                             <li><p><a title="https://quickbooks.intuit.com/payroll/" href="https://quickbooks.intuit.com/payroll/" target="_blank" rel="noopener noreferrer"><u>QuickBooks Payroll</u></a> works for small to midsize businesses - <em>from accountants and financial experts to non-profits, construction companies, and restaurants</em> - to pay your team and streamline your HR tasks to save precious time, from one easy-to-use platform</p></li>
  //                             <li><p>With <u>Payroll Core</u>, you can easily pay your team and have your payroll taxes done for you</p></li>
  //                             <li><p>With <u>Payroll Premium</u>, you can also manage your team, and access HR and employee benefits</p></li>
  //                             <li><p>With <u>Payroll Elite</u>, you'll have access to on-demand experts, simplifying payday, and protecting what matter most</p></li>
  //                             <li><p>QuickBooks Payroll is the #1 payroll provider for small businesses by helping 1.4 million businesses do payroll and file taxes</p></li>
  //                         </ul>
  //                     </li>

  //                     <li>
  //                         <p><strong>QuickBooks gives self-starters the features they need to get ahead</strong></p>
  //                         <ul>
  //                             <li><p><a title="https://go.roiquant.com/get-qb-solopreneurs-exclusive-discount" href="https://go.roiquant.com/get-qb-solopreneurs-exclusive-discount" target="_blank" rel="noopener noreferrer"><u>Solopreneurs</u></a> stay tax-time ready all year, uncover tax deductions, create and send invoices, track your mileage, and keep tabs on your finances on the go</p></li>
  //                             <li><p>Once you connect a bank account, expense tracking helps you separate your personal and business spending with just the swipe of a screen</p></li>
  //                             <li><p>Find an average of USD $30,645 in tax deductions per year</p></li>
  //                             <li><p>Find an average of USD $16,317 in tax savings per year</p></li>
  //                             <li><p>Cut your tax bill by an average of 38% per year</p></li>
  //                         </ul>
  //                     </li>
  //                 </ul>`,
  //   eligibilityCriteria: `
  //       <ul>
  //         <li>
  //           <p><strong>Must be a new</strong>, <em>and was never in the past a paying</em>, <strong>QuickBooks customer</strong></p>
  //         </li>
  //         <li>
  //           <p>This exclusive offer (<em>90% discount for 3 months</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once for first-time purchase</u></strong></p>
  //         </li>
  //         <li>
  //           <p>It is preferred that our affiliated startups purchase with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to purchasing directly</p>
  //         </li>
  //       </ul>`,
  //   footnote: "Available for all customers",
  //   detailModalFootNote: `Reminder: Only available to roiquant customers`,
  // },

  // {
  //   id: "einPresswire_bonusCredits",
  //   listId: 126,
  //   name: "EIN Presswire",
  //   logo: "/partners/ein.png",
  //   offer: "Free bonus credits upon purchase of EIN Presswire",
  //   description: "Free bonus credits upon purchase of EIN Presswire",
  //   categories: ["Press release distribution", "Marketing communications"],
  //   industry: "News syndicates",
  //   sicCode: "7383",
  //   userAvailability: ["global"],
  //   bundleTiers: ["premium"],
  //   longDescription: `
  //   <p>
  //     The
  //     <a href="https://www.einpresswire.com/" target="_blank" rel="noopener noreferrer"><u>EIN Presswire</u></a>
  //     is everyone's internet news presswire, which has quickly become
  //     <a href="https://www.einpresswire.com/testimonials-client-reviews" target="_blank" rel="noopener noreferrer">
  //       <u>a relied upon service for professional communicators</u>
  //     </a>,
  //     and when organizations need to get their news in front of the media,
  //     stakeholders, and the public that goes online looking for news.
  //     Importantly, EIN Presswire's
  //     <a href="https://www.einpresswire.com/pricing" target="_blank" rel="noopener noreferrer">
  //       <u>pricing is incredibly affordable</u>
  //     </a>
  //     due to its own proprietary distribution technologies.
  //   </p>
  //     `,
  //   longOffer: `
  //   <ul>
  //     <li>
  //       <p><strong>Buy Pro+ package (USD $499) and get 1 free extra press release</strong></p>
  //       <ul>
  //         <li>
  //           <p>
  //             Pay USD $499 for 5 press releases (<em>Pro+ package</em>) and get 1
  //             free extra press release (<em>roiquant's perk</em>):
  //             <strong><u>total 6 press releases</u></strong>
  //           </p>
  //         </li>
  //         <li><p>Price per press release: USD $83.17</p></li>
  //         <li>
  //           <p>
  //             E.g., if in a sales promotion where you bought
  //             <strong>5 press releases</strong> and EIN Presswire rewarded you with
  //             <strong>3 free extra press releases</strong> (<em>promo</em>), our
  //             <strong>1 free extra press release</strong> will also be included
  //           </p>
  //         </li>
  //         <li>
  //           <p>
  //             Therefore,
  //             <u>5 press releases</u> (<em>Pro+ package</em>) +
  //             <u>3 free extra press releases</u> (<em>promo</em>) +
  //             <u>1 free extra press release</u> (<em>roiquant's perk</em>) =
  //             <u>total 9 press releases</u>
  //           </p>
  //         </li>
  //         <li><p>Word limit: 1,000 words</p></li>
  //       </ul>
  //     </li>
  //     <li>
  //       <p><strong>Buy Corporate package (USD $999) and get 2 free extra press releases</strong></p>
  //       <ul>
  //         <li>
  //           <p>
  //             Pay USD $999 for 20 press releases (<em>Corporate package</em>) and
  //             get 2 free extra press releases (<em>roiquant's perk</em>):
  //             <strong><u>total 22 press releases</u></strong>
  //           </p>
  //         </li>
  //         <li><p>Price per press release: USD $45.41</p></li>
  //         <li>
  //           <p>
  //             E.g., if in a sales promotion where you bought
  //             <strong>20 press releases</strong> and EIN Presswire rewarded you with
  //             <strong>5 free extra press releases</strong> (<em>promo</em>), our
  //             <strong>2 free extra press releases</strong> will also be included
  //           </p>
  //         </li>
  //         <li>
  //           <p>
  //             Therefore,
  //             <u>20 press releases</u> (<em>Corporate package</em>) +
  //             <u>5 free extra press releases</u> (<em>promo</em>) +
  //             <u>2 free extra press releases</u> (<em>roiquant's perk</em>) =
  //             <u>total 27 press releases</u>
  //           </p>
  //         </li>
  //         <li><p>Word limit: 2,500 words</p></li>
  //       </ul>
  //     </li>
  //     <li>
  //       <p>
  //         Watch your press releases or editorial content
  //         <a href="https://www.einpresswire.com/world-media-directory/" target="_blank" rel="noopener noreferrer">
  //           <u>get distributed on EIN Presswire's market-leading newswires</u>
  //         </a>
  //         picked up by
  //         <a href="https://news.google.com/" target="_blank" rel="noopener noreferrer"><u>Google News</u></a> read by over 400
  //         million people,
  //         <a href="https://www.bing.com/news" target="_blank" rel="noopener noreferrer"><u>Bing News</u></a>, partner sites, by
  //         NewsPlugin for WordPress publishers and by RSS, email, and social media,
  //         as well as
  //         <a href="https://www.einpresswire.com/distribution-microwires" target="_blank" rel="noopener noreferrer">
  //           <u>
  //             EIN Presswire's portfolio of Microwires that provide targeted reach
  //             and SEO benefits
  //           </u>
  //         </a>
  //       </p>
  //     </li>
  //     <li>
  //       <p>
  //         All press release packages include
  //         <a href="https://apnews.com/hub/press-releases/ein-presswire-newsmatics" target="_blank" rel="noopener noreferrer">
  //           <u>AP News</u>
  //         </a>
  //         and
  //         <a href="https://www.nexstar.tv/" target="_blank" rel="noopener noreferrer"><u>Nexstar Media Group</u></a>, accessing
  //         their combined 170 million monthly active users in addition to
  //         distribution to major news sites, major newswires, targeted journalists
  //         and editorial rooms,
  //         <a
  //           href="https://www.bloomberg.com/professional/products/bloomberg-terminal/news/"
  //         >
  //           <u>Bloomberg Terminals</u>
  //         </a>,
  //         <a href="https://muckrack.com/" target="_blank" rel="noopener noreferrer"><u>MuckRack</u></a>, Google News, Google
  //         Search, Bing and other major search engines, as well as social media push
  //       </p>
  //     </li>
  //   </ul>
  //   `,
  //   eligibilityCriteria: `
  //   <ul>
  //     <li>
  //       <p>
  //         <strong>Must be a new</strong>,
  //         <em>and was never in the past a paying</em>,
  //         <strong>EIN Presswire customer</strong>
  //       </p>
  //     </li>
  //     <li>
  //       <p>
  //         This free bonus credits (<em>free extra press releases</em>) is a
  //         <strong>
  //           one-time benefit that can only be redeemed/used
  //           <u>once for first-time purchase</u>
  //       </strong>
  //       </p>
  //     </li>
  //     <li>
  //       <p>
  //         Although the perks (<em>above</em>) can only be redeemed once but if you
  //         continue to <u>make subsequent purchases with our referral link</u>, it
  //         will help us to keep track of our referral performance and may enable us
  //         to offer better benefits in the future
  //       </p>
  //     </li>
  //     <li>
  //       <p>
  //         It is preferred that our affiliated startups purchase with our referral
  //         link to enjoy exclusive offers and special discounts available to our
  //         roiquant community members, as opposed to purchasing directly
  //       </p>
  //     </li>
  //   </ul>
  //   `,
  //   footnote: "Available for all customers",
  //   detailModalFootNote: `Reminder: Only available to roiquant customers`,
  // },
  {
    id: "hubspot",
    listId: 115,
    name: "HubSpot for Startups",
    logo: "/partners/hubspot.png",
    offer: "30%-90% discount on HubSpot",
    description: "30%-90% discount on HubSpot",
    categories: ["CRM software", "Marketing software", "Cloud SaaS"],
    industry: "Services-prepackaged software",
    sicCode: "7372",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: `
      <p>The <a title="https://www.hubspot.com/startups" href="https://www.hubspot.com/startups" target="_blank" rel="noopener noreferrer"><u>HubSpot for Startups program</u></a> provides a huge library of free resources, access to top investors, and a place to meet other passionate founders. Importantly, eligible startups can <strong>save <u>30%-90%</u></strong>on the AI-powered and connected CRM that 258,000+ customers, <em>including some of the fastest-growing startups</em>, trust to increase leads, accelerate sales, and streamline customer service.</p>`,
    longOffer: `
    <ul>
      <li>
          <p><u>If you've raised </u><strong><u>up to USD $2 million</u></strong><u> in funding</u></p>
          <ul>
              <li>
                  <p>For 1st year: get up to <strong>90% discount</strong></p>
              </li>
              <li>
                  <p>For 2nd year: get up to <strong>50% discount</strong></p>
              </li>
              <li>
                  <p>For 3rd year: get up to <strong>25% discount</strong></p>
              </li>
          </ul>
      </li>
      <li>
          <p><u>If you've raised </u><strong><u>over USD $2 million</u></strong><u> in funding</u></p>
          <ul>
              <li>
                  <p>For 1st year: get up to <strong>50% discount</strong></p>
              </li>
              <li>
                  <p>For 2nd year: get up to <strong>25% discount</strong></p>
              </li>
              <li>
                  <p><em>Note: Have not raised series B or beyond</em></p>
              </li>
          </ul>
      </li>
      <li>
          <p><u>If you're simply </u><strong><u>affiliated with one of HubSpot's partner organizations</u></strong></p>
          <ul>
              <li>
                  <p>For 1st year: get up to <strong>30% discount</strong></p>
              </li>
              <li>
                  <p>For 2nd year: get up to <strong>15% discount</strong></p>
              </li>
          </ul>
      </li>
      <li>
          <p><u>HubSpot for Startups: </u><strong><u>exclusive offers and discounts</u></strong></p>
          <ul>
              <li>
                  <p><a title="https://www.hubspot.com/startups/customers/dashboard/offers" href="https://www.hubspot.com/startups/customers/dashboard/offers" target="_blank" rel="noopener noreferrer"><u>Special offers and discounts</u></a> are exclusively available to<strong>HubSpot for Startups members</strong></p>
              </li>
          </ul>
      </li>
      <li>
          <p><strong><u>Valuable education resources and tailored training</u></strong></p>
          <ul>
              <li>
                  <p>Free content, masterclasses, templates, kits, and guides to help you save time and avoid costly mistakes growing your startup</p>
              </li>
              <li>
                  <p>Plus, access to world-class support from HubSpot experts, and more</p>
              </li>
          </ul>
      </li>
      <li>
          <p><strong><u>Integrated platform for startups</u></strong></p>
          <ul>
              <li>
                  <p>Get access to over 1,800+ apps, <em>many at startup-friendly pricing</em>, as well as the opportunity to build an integration on the HubSpot AI-powered platform</p>
              </li>
              <li>
                  <p>Comes with award-winning support and education</p>
              </li>
          </ul>
      </li>
      <li>
        <p><a title="https://www.hubspot.com/products/artificial-intelligence" href="https://www.hubspot.com/products/artificial-intelligence" target="_blank" rel="noopener noreferrer"><strong><u>Breeze AI: boost productivity, scale growth, unlock insights</u></strong></a></p>
        <ul>
          <li>
            <p><a title="https://www.hubspot.com/products/artificial-intelligence/ai-customer-service-agent" href="https://www.hubspot.com/products/artificial-intelligence/ai-customer-service-agent" target="_blank" rel="noopener noreferrer"><u>Breeze Customer Agent</u></a> takes care of high-volume conversations across your go-to-market teams</p>
            <ul>
              <li>
                <p>Breeze Customer Agent is now included as standard in Professional and Enterprise subscriptions</p>
              </li>
            </ul>
          </li>
          <li>
            <p>HubSpot Credits are a flexible, usage-based system for accessing advanced AI features like Breeze Customer Agent</p>
            <ul>
              <li>
                <p>Every Professional and Enterprise subscription include a set allotment of credits to try Breeze Customer Agent immediately, along with Breeze Intelligence</p>
              </li>
            </ul>
          </li>
          <li>
            <p>Enrich your Smart CRM with Breeze Intelligence:</p>
            <ul>
              <li>
                <p><a title="https://www.hubspot.com/products/artificial-intelligence/data-enrichment" href="https://www.hubspot.com/products/artificial-intelligence/data-enrichment" target="_blank" rel="noopener noreferrer"><u>Data enrichment</u></a>: enrich company and contact record to drive decisions and convert top opportunities</p>
              </li>
              <li>
                <p><a title="https://www.hubspot.com/products/artificial-intelligence/intent" href="https://www.hubspot.com/products/artificial-intelligence/intent" target="_blank" rel="noopener noreferrer"><u>Buyer intent</u></a>: discover which companies in your target market are showing intent on your website</p>
              </li>
              <li>
                <p><a title="https://www.hubspot.com/products/artificial-intelligence/form" href="https://www.hubspot.com/products/artificial-intelligence/form" target="_blank" rel="noopener noreferrer"><u>Form shortening</u></a>: build optimized forms that auto-fill information behind the scenes to boost conversion rates</p>
              </li>
            </ul>
          </li>
        </ul>
      </li>
  </ul>
  `,
    eligibilityCriteria: `
      <ul>
        <li><p>You <strong>must be affiliated or associated</strong> with <a title="https://offers.hubspot.com/startups/partner-directory" href="https://offers.hubspot.com/startups/partner-directory" target="_blank" rel="noopener noreferrer"><u>one of HubSpot partners</u></a> (<em>such as roiquant</em>)</p></li>
        <li><p>You <strong>must meet HubSpot for Startups' funding criteria</strong> (<em>as described above</em>)</p></li>
        <li><p>HubSpot for Startups' <strong>discounted pricing only applies to net-new customers</strong></p></li>
        <li>
          <p>This exclusive offer (<em>30%-90% discount</em>) is a <strong>one-time benefit that can only be redeemed/used <u>once for first-time purchase</u></strong></p>
        </li>
        <li><p>Learn more about <a title="https://www.hubspot.com/startups/customers" href="https://www.hubspot.com/startups/customers" target="_blank" rel="noopener noreferrer"><u>HubSpot for Startups from its startups' success stories</u></a></p></li>
        <li>
          <p>It is preferred that our affiliated startups apply with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly</p>
        </li>
      </ul>`,
    footnote: "Available for all customers",
    detailModalFootNote: `Reminder: Only available to roiquant customers`,
  },
  {
    id: "google",
    listId: 128,
    name: "Google for Startups",
    logo: "/partners/google.png",
    offer:
      "Up to US$200,000 Google Cloud credits (up to US$350,000 for AI startups)",
    description:
      "Up to US$200,000 Google Cloud credits (up to US$350,000 for AI startups)",
    categories: ["Cloud computing", "AI/ML platform"],
    industry:
      "Computer processing and data preparation and processing services",
    sicCode: "7374",
    userAvailability: ["global"],
    bundleTiers: ["premium"],
    longDescription: ` 
      <p>From ideation to launch and beyond, build with Google Cloud and get access to the best of <a href="https://about.google/" target="_blank" rel="noopener noreferrer">Google</a>. Early stage startups can get up to US$350,000 in Google Cloud credits through the <a href="https://startup.google.com/cloud/" target="_blank" rel="noopener noreferrer">Google for Startups Cloud Program</a>, and series B+ startups can continue scaling with customized support and solutions. It&rsquo;s time for a new way to cloud.</p>
    `,
    longOffer: `
    <ul>
      <li>
      <p><strong>Join the <u>Start tier</u> if you're in the MVP stage</strong>: partner with Google for Startups to ideate and gain traction with <strong>US$2,000 in Google Cloud credits</strong> to build your foundation</p>
      <ul>
      <li>
      <p>Google Cloud Skills Boost credits</p>
      </li>
      <li>
      <p>Startup Perks</p>
      </li>
      <li>
      <p>Workspace discounts</p>
      </li>
      <li>
      <p><em><u>Note</u>: Start tier is designed for pre-funded startups who have not yet received equity funding</em></p>
      </li>
      </ul>
      </li>
      <li>
      <p><strong>Join the <u>Scale tier</u> if you've raised funding</strong>: scale faster with <strong>US$200,000 in Google Cloud credits (</strong><em><strong>or, up to US$350,000 for AI-first startups</strong></em><strong>)</strong> to get dedicated technical help, exclusive benefits and more to help you grow</p>
      <ul>
      <li>
      <p>Dedicated Startups Success Manager</p>
      </li>
      <li>
      <p>Enhanced support</p>
      </li>
      <li>
      <p>Exclusive startup perks</p>
      </li>
      <li>
      <p>Exclusive Google-wide benefits</p>
      </li>
      <li>
      <p><em><u>Note</u>: Scale tier is designed for funded startups with verified equity funding up to and including series A</em></p>
      </li>
      </ul>
      </li>
      <li>
      <p><strong>Explore more solutions if you're a growth stage startups</strong>: scale your business with customized support and solutions that can help take your startup to the next level</p>
      <ul>
      <li>
      <p>Dedicated Customer Engineers</p>
      </li>
      <li>
      <p>Partner services funds</p>
      </li>
      <li>
      <p>Learning credits</p>
      </li>
      <li>
      <p>Custom agreements</p>
      </li>
      <li>
      <p><em>Note: Growth tier is designed for series B+ startups continuing your growth trajectory through customized cloud solutions designed to improve efficiency, acquire more customers, and increase profit</em></p>
      </li>
      </ul>
      </li>
    </ul>
    `,
    eligibilityCriteria: `
    <ul>
      <li>
      <p><strong>Startups are eligible for <u>Start tier</u></strong></p>
      <ul>
      <li>
      <p>Your technology startup is <strong>not yet funded by an institutional investor</strong></p>
      </li>
      <li>
      <p>Your startup must be <strong>founded in the <u>last 5 years</u></strong> (<em>not more than 5 years old</em>)</p>
      </li>
      <li>
      <p>You must <strong>not yet received Google Cloud credits</strong> beyond the free trial</p>
      </li>
      </ul>
      </li>
      <li>
      <p><strong>Startups are eligible for <u>Scale tier</u></strong></p>
      <ul>
      <li>
      <p>Your technology startup had <strong>received equity funding from preseed to series A</strong> (<em>if series A, raised within the last 12 months</em>) <strong>by an institutional investor or common Web3 funding sources</strong></p>
      </li>
      <li>
      <p>Your startup must be <strong>founded in the <u>last 10 years</u></strong> (<em>not more than 10 years old</em>)</p>
      </li>
      <li>
      <p>You must <strong>not yet received more than US$5,000 in Google Cloud credits</strong></p>
      </li>
      </ul>
      </li>
      <li>
      <p>Your startup <strong>must be a privately held for-profit business </strong>headquartered in a country where <u>Google Cloud services are available</u></p>
      </li>
      <li>
      <p><strong>Website</strong>: a <u>live</u> and <u>functioning</u> website&nbsp;</p>
      </li>
      <li>
      <p><strong>Company LinkedIn</strong>: a valid company profile and a direct association with the person who is applying</p>
      </li>
      <li>
      <p>You <strong>must have a Google Cloud account</strong></p>
      </li>
      <li>
      <p>All Google Cloud credits can be applied towards <a href="https://cloud.google.com/terms/services" target="_blank" rel="noopener noreferrer">Google Cloud Platform Services</a> and <a href="https://cloud.google.com/skus/sku-groups/select-google-cloud-offerings" target="_blank" rel="noopener noreferrer">Select Google Cloud offerings</a></p>
      </li>
      <li>
      <p>It is preferred that our affiliated startups apply with our referral link to enjoy exclusive offers and special discounts available to our roiquant community members, as opposed to applying directly</p>
      </li>
    </ul>`,
    footnote: "Available for global customers",
    detailModalFootNote: "Reminder: Only available to roiquant customers",
  },
];
